import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  Observable,
  pairwise,
  startWith,
  Subscription,
  Subscriber,
  filter,
  of,
  Subject,
} from 'rxjs';
import { distinctUntilChanged, switchMap, map } from 'rxjs/operators';
import { ERRORS, INFOS, SUCCESSES } from 'src/app/common/utils/notification-constants';
import { ModalManagerService } from 'src/app/services/modal-manager.service';
import {
  NotificationToasterService,
  V2NotificationDataBuilder,
  NotificationIconConfig,
  NotificationIconTypes,
  NotificationActionButtonsConfig,
} from 'src/app/services/notification-toaster.service';
import {
  IconBackground,
  IconMessageToasterElement,
} from 'src/app/ui/notification-toaster/icon-message-toaster-element/icon-message-toaster-element.component';

import { PresenceProvider } from 'src/app/services/presence-provider';
import { SessionCallParticipantsService } from 'src/app/services/session-call-participants.service';
import { UserService } from 'src/app/services/user.service';
import { VideoAIService } from 'src/app/services/video-ai.service';

import { ProviderStateService } from 'src/app/services/provider-state.service';
import { v4 as uuidv4 } from 'uuid';
import { ISession, Session } from 'src/app/models/session';
import { SpaceRepository } from 'src/app/state/space.repository';
import { LocalDirection, UiService } from 'src/app/services/ui.service';
import { ActionsQueryParamsService } from 'src/app/services/actions-query-params.service';
import {
  SessionSharedDataService,
  SessionView,
  GALLERY_VIEW_RELATED_VIEWS,
} from 'src/app/services/session-shared-data.service';
import { RealtimeSpaceService } from 'src/app/services/realtime-space.service';
import { VideoCallTracksStateService } from 'src/app/services/video-call-tracks-state.service';
import { SpacePermissionsManagerService } from 'src/app/services/space-permissions-manager.service';
import { WbDialogService } from 'src/app/services/wb-dialog.service';
import {
  GALLERY_VIEW_BAR_HEIGHT,
  NEW_VIDEO_CONTROLS_RESIZABLE_RIGHT_VIEW_MIN_WIDTH,
  RESIZABLE_RIGHT_VIEW_MIN_WIDTH,
  RIGHT_VIEW_BORDER_WIDTH,
  RIGHT_VIEW_CONTAINER_PADDING,
  RIGHT_VIEW_MAX_NUM_TILES_PER_PAGE,
  RIGHT_VIEW_NAVIGATION_BUTTONS_HEIGHT,
  RIGHT_VIEW_NAVIGATION_BUTTONS_MARGIN,
  setTileParameters,
  TilePositionParameters,
  TilesContainerParameters,
} from 'src/app/common/utils/tiles-packing-util';
import { clone, isEqual } from 'lodash';
import { AudioChimes, AudioService } from 'src/app/services/audio.service';
import { DomListenerFactoryService } from 'src/app/services/dom-listener-factory.service';
import { LocalTracksManagerService } from 'src/app/services/local-tracks-manager.service';
import { VirtualBackgroundInsertableStreamService } from 'src/app/services/virtual-background-insertable-stream.service';
import { DeviceType } from 'src/app/common/utils/devices-handle-util';
import { matchI } from 'ts-adt';
import { CelebrationsService } from 'src/app/services/celebrations.service';
import { NavService } from 'src/app/services/nav.service';
import { ForegroundActivityService } from 'src/app/services/foreground-activity.service';
import {
  modifiedDebounceTime,
  modifiedSetTimeout,
  modifiedThrottleTime,
} from 'src/app/utilities/ZoneUtils';
import { DeviceAndBrowserDetectorService } from 'src/app/services/device-and-browser-detector.service';
import { filterNil } from '@ngneat/elf';
import { RtcServiceController } from 'src/app/services/rtc.service';
import { EventStateManagerService } from 'src/app/services/event-state-manager.service';
import { Image } from 'konva/lib/shapes/Image';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { BreakoutRoomsManagementService } from 'src/app/services/breakout-rooms/breakout-rooms-management.service';
import {
  OptimizedGridLayoutService,
  PinnedTileFeedDimensions,
} from 'src/app/services/optimized-grid-layout.service';
import { KeyboardShortcutsCheckService } from 'src/app/services/keyboard-shortcuts-check.service';
import { FollowModeUiService } from 'src/app/services/leader-follow-modes/follow-mode/follow-mode-ui.service';
import { ScreenSizeService } from 'src/app/services/screen-size.service';
import { isObserverCallParticipant } from 'src/app/services/observer-mode.utils';
import { ObserverModeService } from 'src/app/services/observer-mode.service';
import { RaiseHandRepositoryService } from '../services/raise-hand-repository.service';
import {
  CallParticipant,
  ParticipantEvent,
  ParticipantEventAction,
  SubscribeCallQuality,
  TrackType,
} from '../../../common/interfaces/rtc-interface';
import { FLAGS, FlagsService } from '../../../services/flags.service';
import { VideoTrackType, VideoTypes } from '../../../services/session-call-tracks.service';
import { CurrentlyDetectingAudio } from '../../common/volume-detector';
import { User } from '../../../models/user';
import { DevicesManagerService } from '../../../services/devices-manager.service';
import {
  createDefaultUserTile,
  UserTile,
  createDummyUser,
  isSameTile,
} from '../../common/user-tile';
import { EventCategory, SessionEvent } from '../SessionEvent';
import { SpaceLeaderModeService } from '../../../services/space-leader-mode.service';
import { PanelView } from '../../panel/panel.component';
import { Section } from '../../panel/participants-manager/participants-manager.component';
import { SpaceRecordingService } from '../../../services/space-recording.service';
import { FileDetails } from '../../../content/upload-task/upload-task.component';
import { ResourceItemModel } from '../../../models/resource';
import { SpacesService } from '../../../services/spaces.service';
import { ResourcesService } from '../../../services/resources.service';
import { RecordingOutput } from '../../../models/recording';
import { WbRecorderComponent } from '../wb-recorder/wb-recorder.component';
import { ForceScreenShareBlockingModalService } from '../services/force-screen-share-blocking-modal.service';
import { SpaceScreenshareService } from '../../../services/space-screenshare.service';
import { VimeoUploadService } from '../../../services/vimeo-upload.service';
import {
  UploadFailedNotificationStatus,
  UploadFileService,
} from '../../../services/upload-file.service';
import { isBoardRemoteVideo } from '../../common/y-session';
import { BoardRemoteVideo } from '../items-canvas/board-items/board-remote-video';
import { BoardItemsManager } from '../items-canvas/board-items/board-items-manager';
import { ItemModel } from '../../../models/itemModel';
import { ReactionsButtonStateService } from '../services/reactions-button-state.service';
import { HideSelfVideoButtonStateService } from '../services/hide-self-video-button-state.service';
import { EndEventRedirectManagerService } from '../../../services/end-event-redirect-manager.service';
import { GalleryBottomBarStateService } from '../services/gallery-bottom-bar-state.service';
import { PictureInPictureService } from '../services/picture-in-picture.service';
import { RaiseHandButtonStateService } from '../services/raise-hand-button-state.service';
import { OneOnOneModeService } from '../services/one-on-one-mode.service';
import { VideoCallControlsService } from '../services/video-call-controls.service';
import { ChildModeService } from '../../../services/child-mode.service';
import {
  FullScreenAppManagerService,
  isFullScreeningAppInCall,
} from '../services/full-screen-app-manager.service';
import { APP_DEFAULT_HEIGHT, APP_DEFAULT_WIDTH } from '../iframe/additional-apps.utils';
import { VideoCallTileStateManagerService } from '../services/video-call-tile-state-manager.service';
import {
  isSameSessionViewStateForAnnotations,
  ScreenShareAnnotationsManagerService,
} from '../services/screen-share-annotations-manager.service';
import {
  VideoTileMenuAction,
  VideoTileMenuContext,
  VideoTileMenuService,
} from '../services/video-tile-menu.service';
import { ScreenshotService } from '../../../services/screenshot.service';
import { BrowserFullscreenService } from '../services/browser-fullscreen.service';

export enum VideoLayout {
  TOP,
  RIGHT,
  MINIMIZED,
  POPOUT, // This is the pop out state in the parent window
  FLOATING, // This is the pop out state in the floating window
}

export enum PopDirection {
  OUT,
  IN,
}

interface ButtonPosition {
  top: number;
  left: number;
  width?: number;
}

const isTileExpandedView = (view: SessionView): boolean =>
  view === SessionView.EXPANDED || view === SessionView.FULLSCREEN;

@UntilDestroy()
@Component({
  selector: 'app-wb-video-call-participant-id',
  templateUrl: './wb-video-call-participant-id.component.html',
  styleUrls: ['./wb-video-call-participant-id.component.scss'],
  providers: [OneOnOneModeService, BrowserFullscreenService],
  animations: [
    trigger('openClose', [
      state('open', style({ opacity: '0' })),
      state('closed', style({ opacity: '1' })),
      transition('closed => open', [animate('0ms')]),
      transition('open => closed', [animate('100ms')]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WbVideoCallParticipantIdComponent implements OnInit, OnDestroy, OnChanges {
  @Input() users: User[] = [];

  readonly user?: User | null;
  readonly sessionView = SessionView;
  readonly SessionView = SessionView;
  readonly TrackType = TrackType;
  readonly SubscribeCallQuality = SubscribeCallQuality;
  readonly VideoTypes = VideoTypes;
  readonly VideoLayout = VideoLayout;
  readonly isScreenShareAnnotationsEnabled = this.flagsService.isFlagEnabled(
    FLAGS.ENABLE_SCREEN_SHARE_ANNOTATIONS,
  );
  readonly TIMEOUT_PERIOD_DETECT_PAGE_NOT_ACTIVE = 60000;
  readonly LocalDirection = LocalDirection;
  readonly isMobileDevice = this.deviceAndBrowserDetectorService.isMobile();
  // The custom resizer strip overlaps Firefox's native resize corner (Firefox
  // does not support `::-webkit-resizer` to suppress it). Hide the custom
  // strip on Firefox so users resize via the native corner instead.
  readonly isFirefox = this.deviceAndBrowserDetectorService.isFirefox();

  // this is the needed width to make the video controls appear correctly
  // otherwise we need to scale the tower
  readonly rightViewControllerMinNeededWidth = 150.0;
  // this is the needed width to make the tile appear correctly
  // otherwise we need to scale the tile controls inside it
  readonly rightViewMinTileNeededWidth = 120.0;

  private participantLookup: { [key: string]: UserTile[] } = {}; // map of user ids to rtc participant
  private userLookup: { [key: string]: User } = {}; // Map of the user ids to pencil user object
  // Specify how many tiles per page.
  private numberOfUserTilesPerPage = 4;
  private rtcConnected: boolean | undefined = undefined;
  private presentUsersUIDs: Set<string> = new Set(); // holds the ids of the present pencil users from firebase
  private spaceId = '';
  private domListener = this.domListenerFactoryService.createInstance();
  private showOtherParticipantsEmotes = true;
  private lastVideoLayoutPosition: VideoLayout;
  private disableVideoStreams = false;
  private deviceChangeListener: EventListener | null = null;
  private currentLayout = VideoLayout.RIGHT;
  // Used for listening the page is active or not
  private unsubcribeAllVideoFeedsTimeout?: any;
  private pageNotActiveTimoutIsExceed = false;

  private currentActiveScreenSharing: { [participantId: string]: CallParticipant } = {};
  private currentTilesContainerParameters?: TilesContainerParameters;
  private emotesExpiryTimerMap: Map<string, NodeJS.Timer> = new Map();
  private containerResizeObserver?: ResizeObserver;
  private rightSessionsPanelResizeObserver?: ResizeObserver;
  // Used to keep tracking of joined events that its participants don't have an entry inside populated users
  private unhandledJoinedEvents: { [participantId: string]: ParticipantEvent } = {};

  private maxNumOfTilesPerPageRightView = RIGHT_VIEW_MAX_NUM_TILES_PER_PAGE;
  private currentBreakoutRoomId?: string;
  private presenceSubscription?: Subscription;

  private _pipWindow?: Window;

  private _screenShareTileToBePinedByNotification?: UserTile;

  private readonly tileReorderTrigger$ = new Subject<void>();

  isMobileView$ = this.pictureInPictureService.isPIPActive$.pipe(
    switchMap((isPIPActive) =>
      isPIPActive
        ? this.uiService.isPipWindowMobile$
        : this.uiService.isMobile.pipe(distinctUntilChanged()),
    ),
  );

  fullScreenAppTileState$ = this.fullScreenAppManagerService.fullScreenAppTileState$;
  currentSessionView$ = this.videoCallTileStateManagerService.currentSessionView$;
  localParticipantId$ = new BehaviorSubject('');
  numberOfActualPresentParticipants$ = new BehaviorSubject(0);
  numberOfShownPresentParticipants$ = this.oneOnOneModeService.isOneOnOneModeActive$.pipe(
    distinctUntilChanged(),
    switchMap((isOneOnOneModeActive) =>
      isOneOnOneModeActive ? of(1) : this.numberOfActualPresentParticipants$,
    ),
  );
  numberOfTilesForLayout$ = combineLatest([
    this.numberOfShownPresentParticipants$,
    this.fullScreenAppTileState$.pipe(map((currentState) => !!currentState?.appElement)),
  ]).pipe(map(([shownParticipants, shownAppTile]) => shownParticipants + (shownAppTile ? 1 : 0)));
  expandedScreenUser$ = new BehaviorSubject<UserTile | undefined>(undefined);
  // The total number of pages of video tiles we have
  numberOfUserTilesPages$ = new BehaviorSubject(1);
  // Holds the index of the current page of user tiles
  currentUserTilesPageIndex$ = new BehaviorSubject(0);
  isLeaderModeActive$ = new BehaviorSubject(false);
  speakingUser$ = new BehaviorSubject<{ user?: User; isLocal: boolean } | undefined>(undefined);
  startAnimation$ = new BehaviorSubject(false);
  navigationLeftButtonPosition$ = new BehaviorSubject<ButtonPosition | undefined>(undefined);
  navigationRightButtonPosition$ = new BehaviorSubject<ButtonPosition | undefined>(undefined);
  rightViewHeight$ = new BehaviorSubject(0);
  rightViewWidth$ = new BehaviorSubject(0);
  rightViewCurrentColsNum$ = new BehaviorSubject(0);
  directionForLocale$ = new BehaviorSubject<LocalDirection>(LocalDirection.LTR);
  resizedRightViewMinWidth$ = new BehaviorSubject(RESIZABLE_RIGHT_VIEW_MIN_WIDTH);
  participantsArray$ = new BehaviorSubject<readonly UserTile[]>([]);
  readonly isOneOnOneModeActive$ = this.oneOnOneModeService.isOneOnOneModeActive$;
  readonly isOverlayDragging$ = this.oneOnOneModeService.isOverlayDragging$;
  readonly oneOnOneUiVm$ = this.oneOnOneModeService.oneOnOneUiVm$;
  readonly isChildModeActive$ = this.childModeService.isChildModeActive$;

  readonly observerModeActive$ = this.observerModeService.observerModeActive$;

  readonly visibleTilesForLayout$: Observable<readonly UserTile[]> = combineLatest([
    this.participantsArray$,
    this.isOneOnOneModeActive$,
  ]).pipe(
    map(([tiles, isOneOnOne]) => {
      if (!isOneOnOne) {
        return tiles;
      }
      // Filter out local participant VIDEO tile (keep screen share tile)
      return tiles.filter(
        (tile) =>
          !tile.participant$.getValue()?.local || tile.track$.getValue() === TrackType.SCREEN,
      );
    }),
  );

  readonly overlaidSelfTile$: Observable<UserTile | null> = combineLatest([
    this.participantsArray$,
    this.isOneOnOneModeActive$,
  ]).pipe(
    map(([tiles, isOneOnOne]) => {
      if (!isOneOnOne) {
        return null;
      }
      return (
        tiles.find(
          (tile) =>
            tile.participant$.getValue()?.local && tile.track$.getValue() === TrackType.VIDEO,
        ) ?? null
      );
    }),
  );

  readonly largeTile$: Observable<UserTile | null> = combineLatest([
    this.visibleTilesForLayout$,
    this.isOneOnOneModeActive$,
    this.sessionSharedDataService.sessionView.current$,
    this.isMobileView$,
  ]).pipe(
    map(([tiles, isOneOnOne, sessionViewState, isMobile]) => {
      if (!isOneOnOne) {
        return null;
      }

      const isFsCase = this.oneOnOneModeService.isFullScreenAnotherTile(
        sessionViewState.view,
        isMobile,
        sessionViewState.expandedFullScreenChange,
      );

      if (isFsCase && sessionViewState.expandedFullScreenChange) {
        const { tileData } = sessionViewState.expandedFullScreenChange;
        const fullScreenTile = tiles.find((t) => isSameTile(t, tileData));
        return fullScreenTile ?? null;
      }

      const activeTiles = tiles.filter((t) => t.active$.getValue());
      const largeTile = activeTiles[0];

      return largeTile ?? null;
    }),
    distinctUntilChanged((prev, curr) => {
      if (prev === null && curr === null) {
        return true;
      }
      if (prev === null || curr === null) {
        return false;
      }
      return isSameTile(prev, curr);
    }),
  );

  edgeAlignedArrows$ = combineLatest([this.currentSessionView$, this.isMobileView$]).pipe(
    map(
      ([currentSessionView, isMobileView]) =>
        currentSessionView === SessionView.WHITEBOARD ||
        currentSessionView === SessionView.FULLSCREEN_APP ||
        ((currentSessionView === SessionView.GALLERY_VIEW ||
          currentSessionView === SessionView.EXPANDED) &&
          isMobileView),
    ),
  );

  // these two sets updated using events so it is safe to not being subjects
  participantsSpeaking: Set<string> = new Set(); // holds the ids of the participants who are speaking
  screenSharingHasAudio: Set<string> = new Set();

  private preRedirectTaskId?: string;
  private currentPiPParticipantId: string | null = null; // Track current PiP participant ID for cycling

  readonly shouldDisableAnnotations$ =
    this.screenShareAnnotationsManagerService.shouldDisableAnnotations$;

  @ViewChild('appGalleryElementHost')
  set appGalleryElementHostRef(elementRef: ElementRef<HTMLDivElement> | undefined) {
    this.fullScreenAppManagerService.appGalleryElementHost = elementRef;
    this.fullScreenAppManagerService.attachFullscreenAppToGalleryTile();
  }

  constructor(
    public sessionSharedDataService: SessionSharedDataService,
    private realtimeSpaceService: RealtimeSpaceService,
    private userService: UserService,
    public rtcServiceController: RtcServiceController,
    public uiService: UiService,
    private modalManagerService: ModalManagerService,
    private notificationToasterService: NotificationToasterService,
    private translateService: TranslateService,
    public devicesManagerService: DevicesManagerService,
    private sessionCallParticipantsService: SessionCallParticipantsService,
    protected flagsService: FlagsService,
    private videoAiService: VideoAIService,
    private providerState: ProviderStateService,
    private spacePermissionsManagerService: SpacePermissionsManagerService,
    private presenceProvider: PresenceProvider,
    public spaceRepo: SpaceRepository,
    private videoCallTracksStateService: VideoCallTracksStateService,
    private spaceLeaderModeService: SpaceLeaderModeService,
    private wbDialogService: WbDialogService,
    private audioService: AudioService,
    private domListenerFactoryService: DomListenerFactoryService,
    private telemetry: TelemetryService,
    private localTracksManager: LocalTracksManagerService,
    private spaceRecordingService: SpaceRecordingService,
    private spacesService: SpacesService,
    private resourcesService: ResourcesService,
    public navService: NavService,
    private celebrationsService: CelebrationsService,
    private foregroundActivityService: ForegroundActivityService,
    private forceScreenShareBlockingModalService: ForceScreenShareBlockingModalService,
    private zone: NgZone,
    private deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    private spaceScreenshareService: SpaceScreenshareService,
    private vimeoUploadService: VimeoUploadService,
    private uploadService: UploadFileService,
    private reactionsButtonStateService: ReactionsButtonStateService,
    protected hideSelfVideoButtonStateService: HideSelfVideoButtonStateService,
    private endEventRedirectManagerService: EndEventRedirectManagerService,
    private eventStateManagerService: EventStateManagerService,
    protected galleryBottomBarStateService: GalleryBottomBarStateService,
    protected pictureInPictureService: PictureInPictureService,
    private actionsQueryParamsService: ActionsQueryParamsService,
    private breakoutRoomsManagementService: BreakoutRoomsManagementService,
    private optimizedGridService: OptimizedGridLayoutService,
    private keyboardShortcutsCheckService: KeyboardShortcutsCheckService,
    public raiseHandRepositoryService: RaiseHandRepositoryService,
    private raiseHandButtonStateService: RaiseHandButtonStateService,
    private videoAIService: VideoAIService,
    private router: Router,
    private followModeUiService: FollowModeUiService,
    private readonly oneOnOneModeService: OneOnOneModeService,
    protected videoCallControlsService: VideoCallControlsService,
    private screenSizeService: ScreenSizeService,
    private virtualBackgroundInsertableStreamService: VirtualBackgroundInsertableStreamService,
    private readonly childModeService: ChildModeService,
    private fullScreenAppManagerService: FullScreenAppManagerService,
    private videoCallTileStateManagerService: VideoCallTileStateManagerService,
    private screenShareAnnotationsManagerService: ScreenShareAnnotationsManagerService,
    private readonly observerModeService: ObserverModeService,
    private readonly videoTileMenuService: VideoTileMenuService,
    private readonly screenshotService: ScreenshotService,
    private readonly browserFullscreenService: BrowserFullscreenService,
  ) {
    this.user = this.userService.user.getValue()!.user;
    this.userService.rtl.pipe(untilDestroyed(this)).subscribe((isRtl) => {
      this.directionForLocale$.next(isRtl ? LocalDirection.RTL : LocalDirection.LTR);
    });

    this.subscribeToResizableRightViewParams();
    this.subscribeToDisableVideoStreamChanged();

    // Default value
    this.lastVideoLayoutPosition = !this.isMobileView() ? VideoLayout.RIGHT : VideoLayout.TOP;

    this.isMobileView$.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe(() => {
      this.updateVideoLayoutPosition();
    });

    this.listenToEscapeEventToCloseFullScreen();

    this.listenToCtrlPEventForPiP();

    this.reactionsButtonStateService.showOtherParticipantsEmotes$
      .pipe(untilDestroyed(this))
      .subscribe((value) => {
        if (value !== undefined) {
          this.showOtherParticipantsEmotes = value;
        }
      });

    this.sessionSharedDataService.startVideoAnimation.pipe(untilDestroyed(this)).subscribe(() => {
      this.startAnimation$.next(true);
    });

    this.detectPageIsActive();

    // Reset on stop video call RTC
    this.rtcServiceController.resetOnStopVideoCallRTC
      .pipe(untilDestroyed(this))
      .subscribe((reset) => {
        if (reset) {
          this.returnToWhiteboard();
        }
      });

    // ---- Recording Upload ---- //
    this.spaceRecordingService.recordingOutput$
      .pipe(
        switchMap(this.displayFinishedRecordingModal.bind(this)),
        filterNil(),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe((file) => {
        this.uploadRecordingToVimeo(file);
      });

    this.handleNotificationsVerticalPosition();
    this.handleBlockingModelWhenNoScreenShare();
    this.handlePIPListeners();
    this.subscribeToHandRaisedAIObservable();
    this.subscribeToRouterNavigation();
    this.subscribeTo1On1ManualChanges();
    this.subscribeToThrottledTileReorder();
    this.subscribeToRaiseHandChanges();
  }

  /**
   * Single throttled entry point for participant tile reordering so multiple signals
   * (raise hand, future: active speaker, etc.)
   */
  private subscribeToThrottledTileReorder(): void {
    const throttleMs = (this.flagsService.featureFlagsVariables?.vc_tile_ordering?.debounce_time ??
      200) as number;
    this.tileReorderTrigger$
      .pipe(
        modifiedThrottleTime(throttleMs, false, { leading: true, trailing: true }),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.updateParticipantsArray();
      });
  }

  private reorderTiles(): void {
    this.tileReorderTrigger$.next();
  }

  private subscribeTo1On1ManualChanges(): void {
    // needed to update vct when we manually enable / disable 1:1 with no external changes
    this.oneOnOneModeService.forceUpdateTiles$.pipe(untilDestroyed(this)).subscribe(() => {
      this.updateTilesPosition();
    });
  }

  private subscribeToRaiseHandChanges(): void {
    this.raiseHandRepositoryService.orderedRaisedHandUserIds$
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.reorderTiles();
      });
  }

  private subscribeToRouterNavigation(): void {
    // this fix both leave space and send to waiting room scenarios
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.raiseHandButtonStateService.clearRaiseHand();
      });
  }

  private subscribeToHandRaisedAIObservable() {
    this.videoAIService
      .handRaisedObservable()
      .pipe(untilDestroyed(this))
      .subscribe((raisedHand) => {
        if (raisedHand !== this.raiseHandButtonStateService.getUserRaiseHandCurrentValue()) {
          this.raiseHandButtonStateService.toggleRaiseHand();
        }
      });
  }

  private applyOptimizedTileParams(
    tileParams: TilePositionParameters,
    currentSessionView: SessionView,
    tilesOnCurrentPage?: number,
  ): void {
    this.navigationLeftButtonPosition$.next({
      top: tileParams.navigationButtonsCoordinates.topLeftButton,
      left: tileParams.navigationButtonsCoordinates.leftLeftButton,
      width: tileParams.navigationButtonsCoordinates?.widthLeftButton,
    });
    this.navigationRightButtonPosition$.next({
      top: tileParams.navigationButtonsCoordinates.topRightButton,
      left: tileParams.navigationButtonsCoordinates.leftRightButton,
      width: tileParams.navigationButtonsCoordinates?.widthRightButton,
    });

    if (currentSessionView === SessionView.GALLERY_VIEW && tilesOnCurrentPage !== undefined) {
      this.recalculateAnchorLeftLastRow(tileParams, tilesOnCurrentPage);
    }

    this.updateTilesState(tileParams, currentSessionView);
  }

  private computeViewportForGalleryLikeViews(): { viewportWidth: number; viewportHeight: number } {
    // Replicate logic from TilesContainerObserverDirective: margins depend on screen width and PiP state
    const screenWidth =
      document.getElementById('parent-container')?.offsetWidth ||
      (this.pictureInPictureService.isPIPActive
        ? this._pipWindow?.innerWidth || 0
        : window.innerWidth);
    const screenHeight = this.pictureInPictureService.isPIPActive
      ? this._pipWindow?.innerHeight || 0
      : window.innerHeight;
    const topNavBarHeight = this.pictureInPictureService.isPIPActive
      ? 0
      : this.sessionSharedDataService.topControlsHeight;
    const videoControlsGalleryViewHeight =
      document.getElementById('video-controls')?.offsetHeight || GALLERY_VIEW_BAR_HEIGHT;

    const viewportHeight = screenHeight - topNavBarHeight - videoControlsGalleryViewHeight;
    const viewportWidth = screenWidth;

    return { viewportWidth, viewportHeight };
  }

  /**
   * Added this function as a hack to import Image from konva as we found out
   * that zooming out until the web viewer unloaded and zoom in back makes
   * the web viewer browser un-clickable.
   */
  hyperbeamHack() {
    new Image({} as any);
  }

  private handlePIPListeners() {
    this.pictureInPictureService.beforePIPElementMove$
      .pipe(untilDestroyed(this))
      .subscribe(() => this.fullScreenAppManagerService.suspendFullscreenAppInCallForDocumentPiP());

    // to restore the full screen app suspension flag when the PiP open fails
    this.pictureInPictureService.pipOpenFailed$
      .pipe(untilDestroyed(this))
      .subscribe(() =>
        this.fullScreenAppManagerService.restoreFullscreenAppInCallAfterDocumentPiP(),
      );

    this.pictureInPictureService.isPIPActive$
      .pipe(untilDestroyed(this))
      .subscribe((isPIPActive) => {
        if (isPIPActive) {
          this._pipWindow = window.pipWindow;
          this.domListener.add(window.pipWindow, 'resize', this.onPipWindowResize.bind(this));
          if (
            this.sessionSharedDataService.sessionView.getSessionView() !== SessionView.WHITEBOARD &&
            !this.pictureInPictureService.isPIPAutoTriggered()
          ) {
            this.returnToWhiteboard();
          }
        } else {
          this.domListener.remove(this._pipWindow, 'resize');
          this._pipWindow = undefined;
          modifiedSetTimeout(() => {
            this.fullScreenAppManagerService.restoreFullscreenAppInCallAfterDocumentPiP();
          });
        }
      });

    // A manually opened PiP holds the video call container, so gallery-like views would render
    // empty in the main document. Close the PiP instead of leaving both windows in a broken state.
    this.sessionSharedDataService.sessionView.current$
      .pipe(
        map((sessionViewState) => sessionViewState.view),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe((view) => {
        if (
          GALLERY_VIEW_RELATED_VIEWS.includes(view) &&
          this.pictureInPictureService.isPIPActive &&
          !this.pictureInPictureService.isPIPAutoTriggered()
        ) {
          this.pictureInPictureService.closePIP();
        }
      });
  }

  private onPipWindowResize() {
    const isMobileView = (window.pipWindow?.innerWidth ?? 0) <= this.uiService.MOBILE_MAX_WIDTH;
    if (this.uiService.isPipWindowMobile !== isMobileView) {
      this.uiService.isPipWindowMobile = isMobileView;
    }
  }

  private handleBlockingModelWhenNoScreenShare() {
    if (!this.forceScreenShareBlockingModalService.featureEnabled()) {
      return;
    }
    // start subscribing after cam/mic states are ready to make sure that device error helper will be shown firstly
    // as the modals will be shown on a tap operation
    this.localTracksManager.firstDevicesStateAfterJoiningCall
      .pipe(
        switchMap(() =>
          combineLatest([
            this.spaceScreenshareService.numberOfScreenshares$.pipe(map((cnt) => cnt > 0)),
            this.spaceRecordingService.cloudRecordingActive$,
            this.spaceRepo.isCurrentUserHost$,
            this.spaceScreenshareService.localScreenShareMode$,
          ]),
        ),
      )
      .pipe(untilDestroyed(this), modifiedDebounceTime(500), distinctUntilChanged(isEqual))
      .subscribe(
        ([callHasActiveScreenTiles, CloudRecordingActive, isHost, localScreenShareMode]) => {
          this.forceScreenShareBlockingModalService.stateChanged({
            callHasActiveScreenTiles: callHasActiveScreenTiles,
            cloudRecordingActive: CloudRecordingActive,
            isUserHost: isHost,
            localScreenShareMode: localScreenShareMode,
          });
        },
      );
  }

  private handleNotificationsVerticalPosition() {
    combineLatest([
      this.isMobileView$,
      this.providerState.callConnected$,
      this.sessionSharedDataService.controlsLayout,
      this.pictureInPictureService.isPIPActive$,
      this.sessionSharedDataService.sessionView.current$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([isMobileView, callConnected, controlsLayout, isPIPActive, currentView]) => {
        const isGalleryRelatedView = GALLERY_VIEW_RELATED_VIEWS.includes(currentView.view);

        // Show notifications below video carousel only on mobile in WHITEBOARD/FULLSCREEN_APP views
        // For Gallery View related views, show notifications normally (above video controls)
        if (isMobileView && callConnected && !isPIPActive && !isGalleryRelatedView) {
          const root = document.documentElement;
          // 8px margin between the toaster and the video controls
          const extraMargin = 8;
          root.style.setProperty(
            '--toaster-vertical-position',
            `calc(${
              controlsLayout === VideoLayout.MINIMIZED
                ? 'var(--mobile-speaking-indicator-height)'
                : 'var(--mobile-video-box-height)'
            } + var(--mobile-video-controls-box-height)
            + var(--top-bar-v2-height)
            + ${extraMargin}px)`,
          );
        } else {
          const root = document.documentElement;
          root.style.removeProperty('--toaster-vertical-position');
        }
      });
  }

  private subscribeToDisableVideoStreamChanged() {
    this.sessionSharedDataService.disableVideoStreamChanged
      .pipe(map((disableVideoStreamChanged) => disableVideoStreamChanged.disabled))
      .pipe(startWith(null), pairwise(), untilDestroyed(this))
      .subscribe(([prevVal, newVal]) => {
        if (prevVal !== newVal && newVal !== null) {
          this.disableVideoStreams = newVal;
          if (this.disableVideoStreams) {
            this.disableIncomingVideoStreams();
          } else {
            this.enableIncomingVideoStreams();
          }
        }
      });
  }

  private subscribeToResizableRightViewParams() {
    this.spaceRepo.isCurrentUserHost$.pipe(untilDestroyed(this)).subscribe((isHost) => {
      // update VCT minimum width
      const dynamicRightWiewFlagVariables =
        this.flagsService.featureFlagsVariables.dynamic_right_view;

      const isNewRightViewVideoControlsEnabled = this.flagsService.isFlagEnabled(
        FLAGS.ENABLE_RIGHT_VIEW_NEW_VIDEO_CONTROLS,
      );
      const promotedRightViewWidth = isNewRightViewVideoControlsEnabled
        ? (dynamicRightWiewFlagVariables?.resized_right_view_min_width_new_video_controls as number) ||
          NEW_VIDEO_CONTROLS_RESIZABLE_RIGHT_VIEW_MIN_WIDTH
        : (dynamicRightWiewFlagVariables?.resized_right_view_min_width as number) ||
          RESIZABLE_RIGHT_VIEW_MIN_WIDTH;
      this.resizedRightViewMinWidth$.next(promotedRightViewWidth);

      if (isHost) {
        // update maximum VCT tiles per page for hosts
        const promotedMaxNumOfTilesRightViewPerPage =
          dynamicRightWiewFlagVariables?.host_max_page_tiles;
        if (promotedMaxNumOfTilesRightViewPerPage) {
          this.maxNumOfTilesPerPageRightView = promotedMaxNumOfTilesRightViewPerPage as number;
        }
      } else {
        // update maximum VCT tiles per page for non hosts
        const promotedMaxNumOfTilesRightViewPerPage =
          dynamicRightWiewFlagVariables?.participant_max_page_tiles;
        if (promotedMaxNumOfTilesRightViewPerPage) {
          this.maxNumOfTilesPerPageRightView = promotedMaxNumOfTilesRightViewPerPage as number;
        }
      }
    });
  }

  private listenToEscapeEventToCloseFullScreen() {
    this.domListener.add(
      window,
      'keydown.escape',
      (event: KeyboardEvent) => {
        // handle breakout romms in SpaceBreakoutRoomViewComponent
        if (
          this.keyboardShortcutsCheckService.shouldSkipKeyboardShortcuts(event, undefined, true) ||
          this.isLeaderModeActive$.getValue()
        ) {
          return;
        }

        // Re-enter Angular zone before performing actions that require change detection
        this.zone.run(() => {
          const currentView = this.videoCallTileStateManagerService.currentSessionView;

          if (this.closeBrowserFullscreenView()) {
            return;
          }
          if (currentView == SessionView.GALLERY_VIEW) {
            // Return to whiteboard when nothing is pinned
            this.sessionSharedDataService.sessionView.switchToSessionView(SessionView.WHITEBOARD);
          }
        });
      },
      true,
    );
  }

  /**
   * Closes the views that run in browser full screen: unpins the expanded video tile / screen
   * share, or returns a full-screened app in call to the whiteboard.
   * Returns false when neither is shown.
   *
   * Shared by the Esc keydown handler and `exitedByBrowser$`, since the browser consumes Esc
   * while in full screen. Gallery view stays in the keydown handler only: it never enters
   * browser full screen, so Esc reaches the page there and `exitedByBrowser$` can't fire for it.
   */
  private closeBrowserFullscreenView(): boolean {
    if (this.closeExpandedTile()) {
      return true;
    }
    if (this.fullScreenAppManagerService.isFullscreenAppInCallMode()) {
      this.returnToWhiteboard();
      return true;
    }
    return false;
  }

  /**
   * Unpins / exits fullscreen of the expanded video tile or screen share.
   * Returns false when no tile is pinned or full-screened.
   */
  private closeExpandedTile(): boolean {
    const currentView = this.videoCallTileStateManagerService.currentSessionView;
    const expandedScreenUser = this.expandedScreenUser$.getValue();
    if (
      (currentView !== SessionView.FULLSCREEN && currentView !== SessionView.EXPANDED) ||
      !expandedScreenUser
    ) {
      return false;
    }
    if (currentView === SessionView.FULLSCREEN) {
      this.handleFullscreen(expandedScreenUser, true);
    } else {
      this.handleExpand(expandedScreenUser, true);
    }
    return true;
  }

  /**
   * Keeps native browser full screen in sync with pinned / fullscreen video tiles, and closes
   * the tile when the browser exits full screen (Esc).
   * `current$` emits synchronously from `switchToSessionView`, so a pin / fullscreen click
   * still counts as the user gesture the browser requires.
   */
  private syncBrowserFullscreenWithExpandedTile(): void {
    this.sessionSharedDataService.sessionView.current$
      .pipe(
        map(({ view }) => view),
        distinctUntilChanged(
          (previous, current) => isTileExpandedView(previous) === isTileExpandedView(current),
        ),
        untilDestroyed(this),
      )
      .subscribe((view) => {
        if (isTileExpandedView(view)) {
          this.browserFullscreenService.enter({
            view,
            isLeaderModeActive: this.isLeaderModeActive$.getValue(),
          });
        } else {
          this.browserFullscreenService.exit();
        }
      });

    // The browser consumes Esc while in full screen, so mirror the Esc behaviour here
    this.browserFullscreenService.exitedByBrowser$
      .pipe(
        filter(() => !this.isLeaderModeActive$.getValue()),
        untilDestroyed(this),
      )
      .subscribe(() => this.closeBrowserFullscreenView());
  }

  private listenToCtrlPEventForPiP() {
    this.domListener.add(
      window,
      'keydown.control.p',
      (event: KeyboardEvent) => {
        event.preventDefault();
        this.handleCtrlPShortcut();
      },
      true,
    );
  }

  private detectPageIsActive() {
    // If the page is in an iframe, we don't need to detect page activity
    // This is a temporary solution for our iframe use case (Integration Csutomers)
    if (this.deviceAndBrowserDetectorService.isInIframe()) {
      return;
    }

    this.foregroundActivityService.isForegroundInactive$
      .pipe(untilDestroyed(this))
      .subscribe((isInActive) => {
        this.handlePageActivityChange(isInActive);
      });
  }

  private handlePageActivityChange(isInActive: boolean) {
    if (!this.videoCallTileStateManagerService.callConnected) {
      return;
    }
    if (isInActive) {
      this.unsubcribeAllVideoFeedsTimeout = modifiedSetTimeout(() => {
        if (this.pictureInPictureService.isPIPActive) {
          return;
        }
        this.pageNotActiveTimoutIsExceed = true;
        this.unsubscribeAllVideos();
      }, this.TIMEOUT_PERIOD_DETECT_PAGE_NOT_ACTIVE);
    } else {
      if (this.unsubcribeAllVideoFeedsTimeout) {
        clearTimeout(this.unsubcribeAllVideoFeedsTimeout);
        this.unsubcribeAllVideoFeedsTimeout = undefined;
        if (this.pageNotActiveTimoutIsExceed) {
          this.zone.run(() => {
            this.pageNotActiveTimoutIsExceed = false;
            this.resetVideosSubscribtionState();
          });
        }
      }
    }
  }

  // After resize
  private updateVideoLayoutPosition() {
    if (this.sessionSharedDataService.controlsLayout.value != VideoLayout.TOP) {
      // this is the last layout before moving to mobile view
      this.lastVideoLayoutPosition = this.sessionSharedDataService.controlsLayout.value;
    }
    this.sessionSharedDataService.controlsLayout.next(
      !this.isMobileView() ? this.handleSwitchingToLastVideoLayoutPosition() : VideoLayout.TOP,
    );
  }

  // Used to handle the case of entering the space with mobile view width range, then moving to the normal desktop view width range
  private handleSwitchingToLastVideoLayoutPosition(): VideoLayout {
    if (this.lastVideoLayoutPosition === VideoLayout.TOP) {
      return VideoLayout.RIGHT;
    }
    return this.lastVideoLayoutPosition;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.users) {
      this.setUserLookup();
      // If there is a change for populated users & there are unhandled joined events (Due to no entry inside populated users),
      // We will loop through all unhandled joined events to add them as the change (Populated users) may contain an entry for it
      this.addAllUnhandledJoinedEvents();
      this.updateParticipantsArray();
    }
  }

  setUserLookup(): void {
    this.userLookup = this.users?.reduce(
      (dict = {}, currUser: User) => ({ ...dict, [currUser._id]: currUser }),
      {},
    );
  }

  private addAllUnhandledJoinedEvents() {
    if (Object.keys(this.unhandledJoinedEvents).length) {
      for (const unhandledJoinedParticipantEvent of Object.values(this.unhandledJoinedEvents)) {
        this.handleParticipantJoined(unhandledJoinedParticipantEvent);
      }
    }
  }

  ngOnInit(): void {
    this.realtimeSpaceService.service.subscribeToEvent(this, EventCategory.Users);

    combineLatest([
      this.sessionSharedDataService.sessionView.current$,
      this.pictureInPictureService.isPIPActive$,
    ])
      .pipe(pairwise(), untilDestroyed(this))
      .subscribe(([[previousSessionView], [currentSessionView, currentIsPIPActive]]) => {
        const currentView = currentIsPIPActive ? SessionView.GALLERY_VIEW : currentSessionView.view;
        this.videoCallTileStateManagerService.currentSessionView = currentView;
        if (!isSameSessionViewStateForAnnotations(previousSessionView, currentSessionView)) {
          this.fullScreenAppManagerService.updateAppGalleryTileState();
        }
        this.currentUserTilesPageIndex$.next(0);
        // Force updating tiles positions
        // 1) if we are still on expanded view as it won't be triggered by observer directive
        // 2) if we are on fullscreen view and the id changed
        if (
          (previousSessionView.view === SessionView.EXPANDED &&
            this.videoCallTileStateManagerService.currentSessionView === SessionView.EXPANDED) ||
          this.videoCallTileStateManagerService.currentSessionView === SessionView.FULLSCREEN_APP ||
          (currentView === SessionView.FULLSCREEN &&
            previousSessionView.view === currentView &&
            previousSessionView.expandedFullScreenChange?.id !==
              currentSessionView.expandedFullScreenChange?.id)
        ) {
          this.updateTilesPosition();
        }
        // In case of returning back to whiteboard view and the current controls layout is minimized, no need for update current user tiles
        // Just unsubscribe from the previous video streams (Gallery/expanded view)
        if (
          this.videoCallTileStateManagerService.currentSessionView === SessionView.WHITEBOARD &&
          this.sessionSharedDataService.controlsLayout.getValue() === VideoLayout.MINIMIZED
        ) {
          this.unsubscribeAllVideos();
        }
      });

    this.setupFullScreenAppManager();
    this.setupVideoTileMenu();
    this.setupVideoTileMenuActions();
    this.syncBrowserFullscreenWithExpandedTile();

    combineLatest([this.spaceRepo.activeSpace$, this.spaceRepo.activeSpaceCurrentRoomUid$])
      .pipe(untilDestroyed(this))
      .subscribe(([activeSpace, activeSpaceCurrentRoomUid]) => {
        if (!activeSpace) {
          return;
        }

        if (!activeSpaceCurrentRoomUid) {
          return;
        }

        if (
          activeSpace._id !== this.spaceId ||
          activeSpaceCurrentRoomUid !== this.currentBreakoutRoomId
        ) {
          this.handleSpaceOrBreakoutRoomChange(activeSpace, activeSpaceCurrentRoomUid);
        }
      });

    this.sessionSharedDataService.controlsLayout.pipe(untilDestroyed(this)).subscribe((layout) => {
      this.currentLayout = layout;
      if (
        (layout === VideoLayout.RIGHT || layout === VideoLayout.TOP) &&
        this.rtcServiceController.service.isConnected()
      ) {
        this.updateTilesPosition();
      }
      // Optimize the performence by unsubcribing from all video streams once entering minimized view
      if (layout === VideoLayout.MINIMIZED) {
        this.unsubscribeAllVideos();
      }
    });

    this.providerState.callConnected$.pipe(untilDestroyed(this)).subscribe((connected) => {
      this.handleRTCConnectionChange(connected);
      if (connected && !this.deviceChangeListener) {
        this.detectNoMic();
      }
      this.fullScreenAppManagerService.moveFullscreenedAppToCallTileIfNeeded(connected);
      this.resetSessionViewAfterCallDisconnect(connected);

      this.videoCallTileStateManagerService.callConnected = connected;
      this.setupVideoPanelSizeObservable();
      this.setupRightSessionsPanelSizeObservable();

      if (!connected) {
        this.cleanupVideoPiPState();
      }
    });

    this.providerState.localParticipantId$
      .pipe(untilDestroyed(this))
      .subscribe((localParticipantId) => {
        if (
          localParticipantId.length !== 0 &&
          this.localParticipantId$.getValue() !== localParticipantId
        ) {
          this.localParticipantId$.next(localParticipantId);
        }
      });

    this.setupVirtualBackgroundTelemetry();

    this.sessionCallParticipantsService.participantEvents$
      .pipe(untilDestroyed(this))
      .subscribe((event) => {
        if (event && event.participant) {
          switch (event.action) {
            case ParticipantEventAction.JOINED:
              // Close join call panel, and A/V settings once the local user is joined if exist
              this.handleClosingAllActiveJoinCallRelatedModals(event.participant.local);
              this.handleParticipantJoined(event);
              break;
            case ParticipantEventAction.UPDATED: // Used to update the expanded user if exist
              this.handleParticipantUpdated(event);
              break;
            case ParticipantEventAction.LEFT:
              this.handleParticipantLeft(event);
          }
          this.handleParticipantChange(event.participant);
          if (this.isMobileView()) {
            this.updateNumberOfTiles();
          }
        }
      });
    this.spaceLeaderModeService.currentUserIsBeingLed$
      .pipe(untilDestroyed(this), startWith(false), pairwise())
      .subscribe(([currentUserIsBeingLedPrev, currentUserIsBeingLedCurr]) => {
        this.isLeaderModeActive$.next(currentUserIsBeingLedCurr);
        if (currentUserIsBeingLedPrev && !currentUserIsBeingLedCurr) {
          this.resetSessionViewAfterLeaderModeIsDisabled();
        }
      });

    this.sessionSharedDataService.sessionView.current$
      .pipe(untilDestroyed(this))
      .subscribe((sessionViewState) => {
        const expandedFullScreenChange = sessionViewState.expandedFullScreenChange;
        const isFullscreenAppInCall = isFullScreeningAppInCall(sessionViewState);
        if (
          this.fullScreenAppManagerService.isFullScreenAppInCallEnabled() &&
          isFullscreenAppInCall
        ) {
          // During leader mode, expandedFullScreenChange can still carry stale participant tile data
          // (e.g. an older screen-share pin). Ignore that data and keep the webviewer gallery tile as source of truth.
          if (this.expandedScreenUser$.getValue()) {
            this.unsetFullScreen(this.expandedScreenUser$.getValue()!);
            this.expandedScreenUser$.next(undefined);
          }
          return;
        }
        if (!expandedFullScreenChange) {
          if (
            [SessionView.EXPANDED, SessionView.FULLSCREEN].includes(
              this.videoCallTileStateManagerService.currentSessionView,
            )
          ) {
            this.openGalleryView();
          }
          return;
        }

        const { tileData, fullscreen } = expandedFullScreenChange;
        const effectiveTrackType = isFullscreenAppInCall ? TrackType.SCREEN : tileData.trackType;

        const participantTile = this.participantsArray$
          .getValue()
          .find(
            (tile) =>
              tile.participant$.getValue()?.participantId === tileData.participantId &&
              tile.track$.getValue() === effectiveTrackType &&
              tile.user$.getValue()._id === tileData.userId,
          );

        if (participantTile) {
          if (fullscreen) {
            this.handleFullscreenFromLeaderMode(participantTile, fullscreen);
            return;
          }
          if (!fullscreen) {
            this.handleShowingAutoPinNotifRemoteLeaderOn(participantTile);
            this.handleAutoExpand(participantTile, true);
            return;
          }
        }

        if (
          this.followModeUiService.isFollowing() ||
          this.spaceLeaderModeService.currentUserIsBeingLed()
        ) {
          return;
        }

        this.openGalleryView();
      });

    combineLatest([
      this.navService.showSessionAnalytics,
      this.pictureInPictureService.isPIPActive$,
      this.breakoutRoomsManagementService.breakoutRoomsManagementScreenShown$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([showAnalytics, isPIPActive, isBreakoutRoomsManagementScreenShown]) => {
        this.getVideoSectionWidth(showAnalytics, isPIPActive, isBreakoutRoomsManagementScreenShown);
      });

    // Listen for PiP close events
    this.pictureInPictureService.videoPiPClosed$
      .pipe(untilDestroyed(this))
      .subscribe((participantId) => {
        this.updateTilePiPState(participantId, false);
        this.currentPiPParticipantId = null;
      });
  }

  // Used to show auto-pin notification for remote participants if the host enabled leader mode then shared his screen
  // And there is no other screen shares
  // It's done separately to depened on leader mode events not Daily. This is for remote participants ONLY
  private handleShowingAutoPinNotifRemoteLeaderOn(participantTile: UserTile) {
    const leaderModeState = this.spaceLeaderModeService.getSpaceLeaderModeStateUi();
    const currentLeaderOwnsScreenShareTile =
      leaderModeState.currentLeaderUid === participantTile.participant$.getValue()?.userId;
    const noOtherScreenSharingsAreAvailable =
      Object.keys(this.currentActiveScreenSharing).length === 1;
    if (
      !noOtherScreenSharingsAreAvailable ||
      !currentLeaderOwnsScreenShareTile ||
      leaderModeState.currentUserIsLeader
    ) {
      return;
    }
    this.buildScreenSharingStartedNotification(
      this.constructScreenSharingStartedNotificationTitle(participantTile.user$.getValue().name),
      undefined,
    );
    this._screenShareTileToBePinedByNotification = undefined;
  }

  updateTilesContainerParameters(newTilesContainerParameters: any) {
    this.currentTilesContainerParameters = newTilesContainerParameters;
    this.updateTilesPosition();
  }

  updateTilesPosition() {
    if (!this.currentTilesContainerParameters) {
      return;
    }
    this.fullScreenAppManagerService.updateAppGalleryTileState();
    const currentSessionView = this.videoCallTileStateManagerService.currentSessionView;

    // Optimized Expanded view (expanded tile + carousel of remaining tiles)
    if (currentSessionView === SessionView.EXPANDED) {
      const { viewportWidth, viewportHeight } = this.computeViewportForGalleryLikeViews();
      const totalOthers = this.calculateCurrentActiveTilesNum(currentSessionView);
      const expandedTile = this.expandedScreenUser$.getValue();
      const appTileState = this.fullScreenAppTileState$.getValue();
      if (!expandedTile && !appTileState) {
        return;
      }
      let expandedTileDimensions: PinnedTileFeedDimensions | undefined;
      if (expandedTile) {
        const participant = expandedTile.participant$.getValue();
        if (!participant) {
          return;
        }
        expandedTileDimensions = this.getExpandedTileFeedDimensions(expandedTile);
      } else if (appTileState) {
        expandedTileDimensions = {
          width: APP_DEFAULT_WIDTH,
          height: APP_DEFAULT_HEIGHT,
          stretchToFill: true,
        };
      }
      const pinnedTileDimensions = expandedTileDimensions ?? { width: 16, height: 9 };

      const capacityMeta = this.optimizedGridService.getExpandedViewPreferredCarouselCapacity(
        totalOthers,
        { viewportWidth, viewportHeight },
        pinnedTileDimensions,
      );

      const tilesOnCurrentPage = this.applyPagination(totalOthers, capacityMeta.capacity);

      const tileParams = this.optimizedGridService.findOptimalExpandedLayoutForTileCount(
        tilesOnCurrentPage,
        totalOthers,
        { viewportWidth, viewportHeight },
        pinnedTileDimensions,
      );

      if (tileParams) {
        this.applyOptimizedTileParams(tileParams, currentSessionView);
        return;
      }
    }

    // Use optimized grid for gallery view (capacity + per-page optimal layout)
    if (currentSessionView === SessionView.GALLERY_VIEW) {
      const totalParticipants = this.calculateCurrentActiveTilesNum(currentSessionView);

      const { viewportWidth, viewportHeight } = this.computeViewportForGalleryLikeViews();

      const capacity = this.optimizedGridService.getMaxGalleryTilesCapacity({
        viewportWidth,
        viewportHeight,
      });

      const tilesOnCurrentPage = this.applyPagination(totalParticipants, capacity);

      const tileParams = this.optimizedGridService.findOptimalGalleryLayoutForTileCount(
        tilesOnCurrentPage,
        { viewportWidth, viewportHeight },
      );

      this.applyOptimizedTileParams(tileParams, currentSessionView, tilesOnCurrentPage);
      return;
    }

    // Fall back to original logic for non-gallery/right views or if optimization fails
    const tilesContainerParameters = clone(this.currentTilesContainerParameters);
    // Calculate maximum number of tiles that page can hold
    const numberOfUserTilesPerPage = this.setNumberOfUserTilesPerPage(
      tilesContainerParameters,
      currentSessionView,
    );

    // Set the number of tiles that can be visible
    const numOfActiveTiles = this.calculateCurrentActiveTilesNum(currentSessionView);

    this.numberOfUserTilesPerPage = numberOfUserTilesPerPage;
    this.numberOfUserTilesPages$.next(this.calculateNumberOfUserTilesPages(numOfActiveTiles));

    const numberOfTilesInCurrentPage =
      this.currentUserTilesPageIndex$.getValue() === this.numberOfUserTilesPages$.getValue() - 1
        ? numOfActiveTiles - this.calculateNumOfAlreadyViewedTiles()
        : this.numberOfUserTilesPerPage;

    const tilePositionParameters = setTileParameters(
      tilesContainerParameters,
      numberOfTilesInCurrentPage,
      currentSessionView,
      this.isMobileView(),
    );
    if (!tilePositionParameters) {
      return;
    }

    // Set navigation buttons position
    this.navigationLeftButtonPosition$.next({
      top: tilePositionParameters.navigationButtonsCoordinates.topLeftButton,
      left: tilePositionParameters.navigationButtonsCoordinates.leftLeftButton,
    });
    this.navigationRightButtonPosition$.next({
      top: tilePositionParameters.navigationButtonsCoordinates.topRightButton,
      left: tilePositionParameters.navigationButtonsCoordinates.leftRightButton,
    });

    // Set component width & height only if the user is on right view to achieve it's exactly fit the tiles without any extra space
    this.setComponetDimensionsWithRightView(
      currentSessionView,
      tilePositionParameters,
      this.currentTilesContainerParameters?.videoControlsFirstDivHeight ?? 0,
      this.currentTilesContainerParameters?.videoControlsSecondDivHeight ?? 0,
    );

    if (this.currentUserTilesPageIndex$.getValue() >= this.numberOfUserTilesPages$.getValue()) {
      this.currentUserTilesPageIndex$.next(
        Math.max(this.numberOfUserTilesPages$.getValue() - 1, 0),
      );
    }
    // Handle visibility state for each active tile
    this.updateTilesState(tilePositionParameters, currentSessionView);
  }

  // Pagination helper ONLY for the new optimized layouts (Gallery/Expanded)
  // Keeps tiles-per-page/page-count in sync and returns tilesOnCurrentPage
  // TODO: migrate other views to this once legacy logic is refactored
  private applyPagination(totalCount: number, capacity: number): number {
    const isSinglePage = totalCount <= capacity;
    const tilesPerPage = isSinglePage ? totalCount : capacity;
    const totalPages = isSinglePage ? 1 : Math.ceil(totalCount / capacity);

    this.numberOfUserTilesPerPage = tilesPerPage;
    this.numberOfUserTilesPages$.next(totalPages);

    if (this.currentUserTilesPageIndex$.getValue() >= totalPages) {
      this.currentUserTilesPageIndex$.next(Math.max(totalPages - 1, 0));
    }

    return isSinglePage ? totalCount : tilesPerPage;
  }

  private calculateCurrentActiveTilesNum(currentSessionView: SessionView): number {
    // Force the current active tiles to be 1 in case of full screen view to use the same logic as gallery view
    if (
      currentSessionView === SessionView.FULLSCREEN ||
      this.oneOnOneModeService.isOneOnOneModeActive
    ) {
      return 1;
    }
    // Current active tiles (Tiles that can be visible)
    let numOfActiveTiles = this.numberOfActualPresentParticipants$.getValue();
    if (currentSessionView === SessionView.EXPANDED && this.expandedScreenUser$.getValue()) {
      numOfActiveTiles--;
    }
    return numOfActiveTiles;
  }

  private calculateNumberOfUserTilesPages(numOfActiveTiles: number): number {
    return Math.ceil(numOfActiveTiles / this.numberOfUserTilesPerPage);
  }

  private calculateNumOfAlreadyViewedTiles(): number {
    const currentSessionView = this.videoCallTileStateManagerService.currentSessionView;
    // the number of tiles that can be visible
    const numOfActiveTiles = this.calculateCurrentActiveTilesNum(currentSessionView);
    const lastPageTilesCount = numOfActiveTiles % this.numberOfUserTilesPerPage;
    if (
      lastPageTilesCount > 0 &&
      this.numberOfUserTilesPages$.getValue() > 1 &&
      this.currentUserTilesPageIndex$.getValue() == this.numberOfUserTilesPages$.getValue() - 1
    ) {
      // incase last page does not have the maximum page size, we will show remaining items from previous page.
      return (
        this.currentUserTilesPageIndex$.getValue() * this.numberOfUserTilesPerPage -
        (this.numberOfUserTilesPerPage - lastPageTilesCount)
      );
    }
    return this.currentUserTilesPageIndex$.getValue() * this.numberOfUserTilesPerPage;
  }

  private recalculateAnchorLeftLastRow(
    optimalResult: TilePositionParameters,
    tilesOnCurrentPage: number,
  ): void {
    const incompleteLastRow = tilesOnCurrentPage % optimalResult.currentColsNum !== 0;

    if (incompleteLastRow) {
      const lastRowColsNum = tilesOnCurrentPage % optimalResult.currentColsNum;
      optimalResult.anchorLeftLastRow =
        (optimalResult.currentColsNum * optimalResult.currentTileWidth +
          (optimalResult.currentColsNum - 1) * optimalResult.spaceBetweenTiles -
          (lastRowColsNum * optimalResult.currentTileWidth +
            (lastRowColsNum - 1) * optimalResult.spaceBetweenTiles)) /
          2 +
        optimalResult.anchorLeft;
    } else {
      // If last row is complete, use regular anchor left
      optimalResult.anchorLeftLastRow = optimalResult.anchorLeft;
    }
  }

  private setComponetDimensionsWithRightView(
    currentSessionView: SessionView,
    tilePositionParameters: TilePositionParameters,
    videoControlsFirstDivHeight: number,
    videoControlsSecondDivHeight: number,
  ) {
    if (this.isRightView(currentSessionView)) {
      const newRightViewWidth =
        tilePositionParameters.currentColsNum * tilePositionParameters.currentTileWidth +
        (tilePositionParameters.currentColsNum - 1) * tilePositionParameters.spaceBetweenTiles +
        2 * RIGHT_VIEW_CONTAINER_PADDING +
        2 * RIGHT_VIEW_BORDER_WIDTH;
      if (!this.currentTilesContainerParameters?.skipSettingComponentDimsWidth) {
        if (newRightViewWidth === this.rightViewWidth$.getValue()) {
          this.rightViewWidth$.next(newRightViewWidth + 0.0000001);
        } else {
          this.rightViewWidth$.next(newRightViewWidth);
        }
      }
      // Top portion: border + top padding + tiles + video controls
      let newRightViewHeight =
        videoControlsFirstDivHeight +
        videoControlsSecondDivHeight +
        tilePositionParameters.currentRowsNum * tilePositionParameters.currentTileHeight +
        (tilePositionParameters.currentRowsNum - 1) * tilePositionParameters.spaceBetweenTiles +
        RIGHT_VIEW_CONTAINER_PADDING +
        2 * RIGHT_VIEW_BORDER_WIDTH;

      // Bottom portion: either nav area (margin + buttons + margin) or just bottom padding
      if (this.numberOfUserTilesPages$.getValue() > 1) {
        newRightViewHeight +=
          RIGHT_VIEW_NAVIGATION_BUTTONS_HEIGHT + 2 * RIGHT_VIEW_NAVIGATION_BUTTONS_MARGIN;
      } else {
        newRightViewHeight += RIGHT_VIEW_CONTAINER_PADDING;
      }

      if (newRightViewHeight !== this.rightViewHeight$.getValue()) {
        this.rightViewHeight$.next(newRightViewHeight);
      }

      this.rightViewCurrentColsNum$.next(tilePositionParameters.currentColsNum);
    }
  }

  private isRightView(currentSessionView: SessionView): boolean {
    return (
      (currentSessionView === SessionView.WHITEBOARD ||
        currentSessionView === SessionView.FULLSCREEN_APP) &&
      this.sessionSharedDataService.controlsLayout.getValue() === VideoLayout.RIGHT
    );
  }

  private updateTilesState(
    tilePositionParameters: TilePositionParameters,
    currentSessionView: SessionView,
  ) {
    let activeTileIndex = -1;
    let visibleTileIndex = -1;

    const startIndex = this.calculateNumOfAlreadyViewedTiles();
    const endIndex = startIndex + this.numberOfUserTilesPerPage;

    const currentActiveRemoteVideoOnCanvas = this.checkRemoteVideoStatusOnCanvas();

    const isOneOnOneModeActive = this.oneOnOneModeService.isOneOnOneModeActive;

    this.handleUpdateAppTileState(tilePositionParameters);

    for (const tile of this.participantsArray$.getValue()) {
      const skipLayoutCalculations =
        isOneOnOneModeActive &&
        !!tile.participant$.getValue()?.local &&
        tile.track$.getValue() === TrackType.VIDEO;

      if (skipLayoutCalculations) {
        // Keep the self-tile subscribed/active for the overlay, but exclude it from the main layout.
        tile.visible$.next(true);
        tile.visibilityIndex$.next(0);
        this.updateTileVisibility(tile, currentActiveRemoteVideoOnCanvas);
        continue;
      }

      if (tile.active$.getValue()) {
        if (
          this.handleUpdateTilePositionFullScreen(tile, tilePositionParameters, currentSessionView)
        ) {
          continue;
        }

        if (
          this.handleUpdateTilePositionExpanded(tile, tilePositionParameters, currentSessionView)
        ) {
          continue;
        }

        activeTileIndex++;

        const isVisible = activeTileIndex >= startIndex && activeTileIndex < endIndex;
        if (isVisible) {
          visibleTileIndex++;
          tile.visibilityIndex$.next(visibleTileIndex);
          const [top, left] = this.calculateTopAndLeftValues(
            visibleTileIndex,
            tilePositionParameters,
          );
          this.updateTilePosition(
            tile,
            tilePositionParameters.currentTileWidth,
            tilePositionParameters.currentTileHeight,
            top,
            left,
          );
        } else {
          tile.visible$.next(false);
        }
      } else {
        tile.visible$.next(false);
      }

      this.updateTileVisibility(tile, currentActiveRemoteVideoOnCanvas);
    }
  }

  private handleUpdateAppTileState(tilePositionParameters: TilePositionParameters) {
    if (!this.fullScreenAppManagerService.isFullScreenAppInCallEnabled()) {
      return;
    }
    const currentAppTileState = this.fullScreenAppTileState$.getValue();
    if (!currentAppTileState) {
      return;
    }
    this.fullScreenAppTileState$.next({
      ...currentAppTileState,
      visible: true,
      top: tilePositionParameters.anchorTopExpanded,
      left: tilePositionParameters.anchorLeftExpanded,
      width: tilePositionParameters.expandedTileWidth,
      height: tilePositionParameters.expandedTileHeight,
    });
  }

  private handleUpdateTilePositionFullScreen(
    userTile: UserTile,
    tilePositionParameters: TilePositionParameters,
    currentSessionView: SessionView,
  ): boolean {
    if (currentSessionView === SessionView.FULLSCREEN) {
      if (this.isParticipantExpanded(userTile)) {
        this.updateTilePosition(
          userTile,
          tilePositionParameters.currentTileWidth,
          tilePositionParameters.currentTileHeight,
          tilePositionParameters.anchorTop,
          tilePositionParameters.anchorLeft,
        );
      } else {
        userTile.visible$.next(false);
      }
      this.updateTileVisibility(userTile);
      return true;
    }
    return false;
  }

  private handleUpdateTilePositionExpanded(
    userTile: UserTile,
    tilePositionParameters: TilePositionParameters,
    currentSessionView: SessionView,
  ): boolean {
    if (currentSessionView === SessionView.EXPANDED && this.isParticipantExpanded(userTile)) {
      this.updateTilePosition(
        userTile,
        tilePositionParameters.expandedTileWidth,
        tilePositionParameters.expandedTileHeight,
        tilePositionParameters.anchorTopExpanded,
        tilePositionParameters.anchorLeftExpanded,
      );
      this.updateTileVisibility(userTile);
      return true;
    }
    return false;
  }

  private calculateTopAndLeftValues(
    visibleTileIndex: number,
    tilePositionParameters: TilePositionParameters,
  ): number[] {
    const colPosition = visibleTileIndex % tilePositionParameters.currentColsNum;
    const rawPosition = Math.floor(visibleTileIndex / tilePositionParameters.currentColsNum);

    let left: number;
    const top =
      rawPosition * tilePositionParameters.currentTileHeight +
      rawPosition * tilePositionParameters.spaceBetweenTiles +
      tilePositionParameters.anchorTop;

    if (
      rawPosition === tilePositionParameters.currentRowsNum - 1 &&
      tilePositionParameters.anchorLeftLastRow
    ) {
      left =
        colPosition * tilePositionParameters.currentTileWidth +
        colPosition * tilePositionParameters.spaceBetweenTiles +
        tilePositionParameters.anchorLeftLastRow;
    } else {
      left =
        colPosition * tilePositionParameters.currentTileWidth +
        colPosition * tilePositionParameters.spaceBetweenTiles +
        tilePositionParameters.anchorLeft;
    }
    return [top, left];
  }

  private isParticipantExpanded(userTile: UserTile): boolean {
    return !!(
      this.expandedScreenUser$.getValue()?.participant$.getValue() &&
      this.expandedScreenUser$.getValue()?.participant$.getValue()?.participantId ===
        userTile.participant$.getValue()?.participantId &&
      this.expandedScreenUser$.getValue()?.track$.getValue() === userTile.track$.getValue()
    );
  }

  private updateTilePosition(
    userTile: UserTile,
    newWidth: number | undefined,
    newHeight: number | undefined,
    newTop: number | undefined,
    newLeft: number | undefined,
  ) {
    userTile.width$.next(newWidth);
    userTile.height$.next(newHeight);
    userTile.top$.next(newTop);
    userTile.left$.next(newLeft);
    userTile.visible$.next(true);
  }

  private getExpandedTileFeedDimensions(expandedTile: UserTile): PinnedTileFeedDimensions {
    const participantId = expandedTile.participant$.getValue()?.participantId ?? '';
    const isScreen = expandedTile.track$.getValue() === TrackType.SCREEN;

    // First, try to get dimensions directly from the video track settings
    let actualDimensions: PinnedTileFeedDimensions | undefined;

    const tracksState =
      this.videoCallTracksStateService.participantTracksStateMap.get(participantId)?.value;
    const mediaTrack: MediaStreamTrack | null | undefined = tracksState
      ? isScreen
        ? tracksState.getCurrentValueScreenVideoTrackSubject()
        : tracksState.getCurrentValueVideoTrackSubject()
      : undefined;

    const settings = (mediaTrack as any)?.getSettings?.();
    if (settings?.width && settings?.height) {
      actualDimensions = {
        width: settings.width,
        height: settings.height,
      };
    }

    // Fallback to RTC service if track settings not available
    if (!actualDimensions || actualDimensions.width === 0 || actualDimensions.height === 0) {
      actualDimensions = this.rtcServiceController.service.getVideoDimensionsByIdentity(
        participantId,
        expandedTile.track$.getValue(),
      );
    }

    return actualDimensions;
  }

  private setNumberOfUserTilesPerPage(
    tilesContainerParameters: TilesContainerParameters,
    currentSessionView: SessionView,
  ): number {
    switch (currentSessionView) {
      case SessionView.WHITEBOARD:
      case SessionView.FULLSCREEN_APP:
        return Math.min(
          tilesContainerParameters.maxColsNum * tilesContainerParameters.maxRowsNum,
          this.maxNumOfTilesPerPageRightView,
        );
      case SessionView.GALLERY_VIEW:
      case SessionView.FULLSCREEN:
        return tilesContainerParameters.maxColsNum * tilesContainerParameters.maxRowsNum;
      case SessionView.EXPANDED:
        return tilesContainerParameters.maxColsNum;
      default:
        return 1;
    }
  }

  // Used for optimizations -> Unsubcribe for all video feeds once:
  // 1. Entering the minimized view
  // 2. Page isn't active for more than 1 minute
  private unsubscribeAllVideos() {
    const currentActiveRemoteVideoOnCanvas = this.checkRemoteVideoStatusOnCanvas();
    for (const tile of this.participantsArray$.getValue()) {
      // If this video feed is inserted into canvas, then skip unsubscribing from it
      if (
        currentActiveRemoteVideoOnCanvas.has(
          `${tile.participant$.getValue()?.participantId}:${tile.track$.getValue()}`,
        )
      ) {
        continue;
      }
      if (
        tile.active$.getValue() &&
        tile.participant$.getValue()?.participantId &&
        tile.visible$.getValue() &&
        !tile.pipVideo$.getValue()
      ) {
        this.videoCallTracksStateService.updateTrackSubscription(
          tile.participant$.getValue()?.participantId,
          tile.track$.getValue(),
          false,
          tile.participant$.getValue().local,
        );
      }
    }
  }

  private checkRemoteVideoStatusOnCanvas(): Set<string> {
    return new Set(
      this.sessionSharedDataService.fabricCanvas
        ?.getObjects()
        .filter((object) => isBoardRemoteVideo(object))
        .map((boardItem) => (boardItem as BoardRemoteVideo).getContentId()),
    );
  }

  protected addRemoteVideo(participantId: string, trackType: VideoTrackType) {
    BoardItemsManager.instance?.insertNewBoardItem<ItemModel.RemoteVideo>(ItemModel.RemoteVideo, {
      participantId,
      trackType,
    });
  }

  private resetVideosSubscribtionState() {
    for (const tile of this.participantsArray$.getValue()) {
      if (
        tile.active$.getValue() &&
        tile.participant$.getValue()?.participantId &&
        tile.visible$.getValue()
      ) {
        this.videoCallTracksStateService.updateTrackSubscription(
          tile.participant$.getValue()?.participantId,
          tile.track$.getValue(),
          true,
          tile.participant$.getValue().local,
        );
      }
    }
  }

  private setupVirtualBackgroundTelemetry(): void {
    this.localParticipantId$
      .pipe(
        untilDestroyed(this),
        filter((id) => id.length > 0),
        switchMap((localParticipantId) =>
          this.videoCallTracksStateService
            .getPariticpantTrackPlayingObservable(localParticipantId, TrackType.VIDEO, this)
            .pipe(distinctUntilChanged()),
        ),
      )
      .subscribe((isPlaying) => {
        this.telemetry.setSessionVars({
          virtual_background_active:
            isPlaying && this.virtualBackgroundInsertableStreamService.isVirtualBackgroundMode(),
        });
      });
  }

  async ngOnDestroy(): Promise<void> {
    this.fullScreenAppManagerService.cleanupFullScreenAppManager();
    if (this.expandedScreenUser$.getValue()) {
      this.unsetFullScreen(this.expandedScreenUser$.getValue()!);
    }
    this.cleanupVideoPiPState();
    this.domListener.clear();
    this.realtimeSpaceService.service.unSubscribeFromEvent(this, EventCategory.Users);
    this.devicesManagerService.unsubscribeFromDeviceChange(this.deviceChangeListener);
    this.containerResizeObserver?.disconnect();
    this.rightSessionsPanelResizeObserver?.disconnect();
    this.spaceRecordingService.clearRecordingOutput();
    if (this.preRedirectTaskId) {
      this.endEventRedirectManagerService.removePreRedirectTask(this.preRedirectTaskId);
    }
    this.pictureInPictureService.cleanupDocumentPiPState();
  }

  // participant state functions
  handleParticipantJoined(event: ParticipantEvent): void {
    const participant = event.participant;

    const user = participant.agentInfo
      ? createDummyUser(participant)
      : this.userLookup[participant.userId];

    if (isObserverCallParticipant(participant)) {
      if (!participant.local) {
        this.telemetry.logBasicEvent('[observer_flow]', {
          action: 'received_remote_observer_participant_joined_event',
          participantId: participant.participantId,
        });
      }
      return;
    }

    if (this.skipAddingJoinedParticipant(participant)) {
      if (!this.userLookup[participant.userId]) {
        // If we will skip due to no entry inside populated users, we will keep track of this unhandled joined event till syncing latest space state
        this.unhandledJoinedEvents[participant.participantId] = event;
        this.telemetry.event(
          '[Populated users issue] A User is not found on participant joined event',
          {
            event,
          },
        );
      }
      return;
    }

    if (!(participant.participantId in this.participantLookup)) {
      const videoTile: UserTile = createDefaultUserTile(user, participant, TrackType.VIDEO, true);
      const screenTile: UserTile = createDefaultUserTile(
        user,
        participant,
        TrackType.SCREEN,
        false,
      );
      this.participantLookup[participant.participantId] = [videoTile, screenTile];
      // To cover any possible race conditions if the tiles are created before ParticipantTracksState object is created successfully
      this.videoCallTracksStateService.createParticipantTracksState(participant.participantId);
      this.updateParticipantsArray();
    }
  }

  private skipAddingJoinedParticipant(participant: CallParticipant): boolean {
    // 1. If the participant has no participantId, we skip it
    // 2. If the partipant is not an agent and they are not in the userLookup, we skip it
    return (
      !participant?.participantId ||
      (!this.userLookup[participant.userId] && !participant.agentInfo)
    );
  }

  handleClosingAllActiveJoinCallRelatedModals(isJoinedParticipantLocal: boolean | undefined) {
    if (isJoinedParticipantLocal) {
      const joinCallPanelRef = this.wbDialogService.joinCallDialogRef;
      const spaceDeviceModalRef = this.wbDialogService.spaceDeviceModalRef;

      if (joinCallPanelRef) {
        joinCallPanelRef.close();
      }
      if (spaceDeviceModalRef) {
        spaceDeviceModalRef.close();
      }
    }
  }

  handleParticipantUpdated(event: ParticipantEvent) {
    if (
      this.expandedScreenUser$.getValue()?.participant$.getValue()?.participantId ===
      event.participant.participantId
    ) {
      this.expandedScreenUser$.getValue()!.participant$.next(event.participant);
    }
  }

  async handleParticipantLeft(event: ParticipantEvent): Promise<void> {
    if (event.participant.participantId) {
      // If the participant who linked to an unhandled joined event left, no need to keep it
      delete this.unhandledJoinedEvents[event.participant.participantId];
      // In case of the local user left the call, we will reset the component state to avoid any regressions
      if (event.participant.local) {
        this.hideSelfVideoButtonStateService.resetHideSelfViewState();
        this.resetValuesAfterLocalUserDisconnected();
      } else {
        delete this.participantLookup[event.participant.participantId];

        if (
          this.expandedScreenUser$.getValue()?.participant$.getValue()?.participantId ===
          event.participant.participantId
        ) {
          if (this.expandedScreenUser$.getValue()) {
            this.unsetFullScreen(this.expandedScreenUser$.getValue()!);
            this.expandedScreenUser$.next(undefined);
          }
          this.sessionSharedDataService.sessionView.switchToSessionView(SessionView.GALLERY_VIEW);
        }
        await this.updateParticipantsArray();
      }

      if (this.currentPiPParticipantId === event.participant.participantId) {
        this.closeCurrentVideoPiP();
      }
    }
  }

  private resetValuesAfterLocalUserDisconnected() {
    this.raiseHandButtonStateService.clearRaiseHand();

    this.participantLookup = {};
    this.localParticipantId$.next('');
    // Reseting the last local participant ID
    this.providerState.resetLocalParticipantIdBehaviorSub();
    this.participantsArray$.next([]);
    this.numberOfUserTilesPages$.next(1);
    this.numberOfUserTilesPerPage = 4;
    this.currentUserTilesPageIndex$.next(0);
    this.participantsSpeaking = new Set();
    this.screenSharingHasAudio = new Set();
    this.numberOfActualPresentParticipants$.next(0);
    this.expandedScreenUser$.next(undefined);
    this.currentActiveScreenSharing = {};
    this.unhandledJoinedEvents = {};

    // to make the modal appear that suggest share screen if screen share not forced
    // when joining a call again
    this.forceScreenShareBlockingModalService.resetStatusOnLeaveCall();
  }

  public handleParticipantChange(participant: CallParticipant): void {
    const tiles = this.participantLookup?.[participant?.participantId ?? ''];
    if (tiles) {
      const screenTile = tiles[1];
      const oldScreenState = screenTile.active$.getValue();
      this.updateScreenSharingTileState(tiles, participant);
      if (oldScreenState !== screenTile.active$.getValue()) {
        this.fireAudioChimeScreenSharingIfActive(oldScreenState, screenTile);
        if (
          oldScreenState &&
          this.expandedScreenUser$.getValue()?.track$.getValue() === TrackType.SCREEN &&
          screenTile?.participant$.getValue()?.participantId ===
            this.expandedScreenUser$.getValue()?.participant$.getValue()?.participantId
        ) {
          this.unsetFullScreen(screenTile);
          this.expandedScreenUser$.next(undefined);
          if (
            [SessionView.EXPANDED, SessionView.FULLSCREEN].includes(
              this.sessionSharedDataService.sessionView.getSessionView(),
            )
          ) {
            this.sessionSharedDataService.sessionView.switchToSessionView(SessionView.GALLERY_VIEW);
          }
        }
        this.handleAutoPinScreenSharing(oldScreenState, screenTile);
      }
    }
    this.dismissOrphanPinScreenShareNotification(participant);
  }

  private dismissOrphanPinScreenShareNotification(participant: CallParticipant) {
    if (
      !this._screenShareTileToBePinedByNotification ||
      participant.participantId !==
        this._screenShareTileToBePinedByNotification.participant$.value.participantId
    ) {
      return;
    }
    const participantHasTiles = this.participantLookup[participant.participantId];
    if (!participantHasTiles || !this.rtcServiceController.trackIsShared(participant.screen)) {
      this.notificationToasterService.dismissNotificationsByCode([INFOS.SCREEN_SHARING_STARTED]);
    }
  }

  private updateScreenSharingTileState(tiles: UserTile[], participant: CallParticipant) {
    tiles.forEach((tile) => {
      tile.participant$.next(participant);
      if (tile.track$.getValue() === TrackType.SCREEN) {
        if (this.rtcServiceController.trackIsShared(tile.participant$.getValue().screen)) {
          if (this.disableCurrentActiveScreenSharing(participant)) {
            this.rtcServiceController.service.stopScreenShare();
            tile.active$.next(false);
          } else {
            this.currentActiveScreenSharing[tile.participant$.getValue().participantId] =
              tile.participant$.getValue();
            tile.active$.next(true);
            // this is to handle a case where the leader expand/fullscreen the tile before the tile
            // become active for the leaded person so we will follow the leader state once the tile become active
            this.followLeaderModeStateOfTheTile(tile);
          }
        } else {
          delete this.currentActiveScreenSharing[tile.participant$.getValue().participantId];
          tile.active$.next(false);
        }
      }
    });
  }

  private followLeaderModeStateOfTheTile(tile: UserTile) {
    const expandedFullScreenChange =
      this.spaceLeaderModeService.getSpaceLeaderModeStateUi().expandAndFullScreenChange;
    if (!expandedFullScreenChange || !this.spaceLeaderModeService.currentUserIsBeingLed()) {
      return;
    }
    const { tileData, fullscreen } = expandedFullScreenChange;
    if (
      tile.participant$.getValue()?.participantId === tileData.participantId &&
      tile.track$.getValue() === tileData.trackType &&
      tile.user$.getValue()._id === tileData.userId
    ) {
      this.sessionSharedDataService.sessionView.switchToSessionView(
        fullscreen ? SessionView.FULLSCREEN : SessionView.EXPANDED,
        {
          tileData,
          id: uuidv4(),
          fullscreen,
        },
      );
    }
  }

  // Used to detect a participant pressed on share screen button, and while choosing which window/tab to share, a host disabled the screen sharing permission
  private disableCurrentActiveScreenSharing(participant: CallParticipant): boolean {
    return !!participant.local && !this.spacePermissionsManagerService.canShareScreen();
  }

  private fireAudioChimeScreenSharingIfActive(oldScreenState: boolean, screenTile: UserTile) {
    if (screenTile.active$.getValue() && !oldScreenState) {
      this.audioService.playAudio(AudioChimes.screenShare);
    }
  }

  private handleAutoPinScreenSharing(oldScreenState: boolean, screenTile: UserTile) {
    if (!this.spaceRepo.activeSpace) {
      return;
    }
    const isScreenTileActive = screenTile.active$.getValue() && !oldScreenState;
    const isLocalUserOwnsCurrentScreenSharing =
      screenTile.participant$.getValue()?.userId === this.user?._id;
    const noOtherScreenSharingsAreAvailable =
      Object.keys(this.currentActiveScreenSharing).length === 1;
    const leaderModeStateUi = this.spaceLeaderModeService.getSpaceLeaderModeStateUi();
    const isCurrentUserHost = Session.isOwnedByUser(this.spaceRepo.activeSpace, this.user);
    const isUserHost = Session.isOwnedByUser(
      this.spaceRepo.activeSpace,
      screenTile.user$.getValue(),
    );

    // Detect if it's possible to auto-pin the current screen sharing while the leader mode is off
    const autoPinScreenSharingExistLeaderModeOff =
      !leaderModeStateUi.currentLeaderUid &&
      isScreenTileActive &&
      noOtherScreenSharingsAreAvailable;

    // Detect if it's possible to auto-pin the current screen sharing while the leader mode is on
    // Last condition is introduced to make the current screen sharing auto-pinned for both sides (Sender[Current leader who shared his screen], and recevier[Other hosts, participants]).
    // To have a consistancy across all cases by depending on Daily events for auto-pinning feature
    const autoPinScreenSharingExistLeaderModeOn =
      leaderModeStateUi.currentLeaderUid &&
      isScreenTileActive &&
      noOtherScreenSharingsAreAvailable &&
      isLocalUserOwnsCurrentScreenSharing &&
      leaderModeStateUi.currentUserIsLeader;

    // Used to suggest auto-pinning with the current leader if another HOST shares his screen
    const suggestPinAnotherHostScreenShareLeaderModeOn =
      leaderModeStateUi.currentLeaderUid !== undefined &&
      isScreenTileActive &&
      !isLocalUserOwnsCurrentScreenSharing &&
      leaderModeStateUi.currentUserIsLeader &&
      isUserHost;

    // Used for auto-pin action
    this.updateNumberOfTiles(
      (autoPinScreenSharingExistLeaderModeOff || autoPinScreenSharingExistLeaderModeOn) &&
        !this.dismissShowingOpenPermissionsPanelNotification(isUserHost, isCurrentUserHost)
        ? screenTile
        : undefined,
    );

    if (
      this.notShowingAutopinNotification(
        isUserHost,
        isCurrentUserHost,
        isLocalUserOwnsCurrentScreenSharing,
        isScreenTileActive,
        leaderModeStateUi.currentLeaderUid,
        autoPinScreenSharingExistLeaderModeOn,
        suggestPinAnotherHostScreenShareLeaderModeOn,
      )
    ) {
      return;
    }

    if (!isLocalUserOwnsCurrentScreenSharing && isScreenTileActive) {
      this.handleShowingScreenSharingStartedNotification(
        isCurrentUserHost,
        isUserHost,
        screenTile,
        autoPinScreenSharingExistLeaderModeOff,
        suggestPinAnotherHostScreenShareLeaderModeOn,
        leaderModeStateUi.currentLeaderUid !== undefined,
      );
    }
  }

  // Used to avoid showing the notification if:
  // 1. Local user who shares his screen
  // 2. Screen sharing is gone
  // 3. If the leader mode is active and the leader/another HOST isn't sharing his screen
  // 4. not showing "Open permissions panel" notification in case of
  //    a participant pressed on share screen button, and while choosing which window/tab to share, a host disabled the screen sharing permission
  private notShowingAutopinNotification(
    isUserHost: boolean,
    isCurrentUserHost: boolean,
    isLocalUserOwnsCurrentScreenSharing: boolean,
    isScreenTileActive: boolean,
    currentLeaderUid: string | undefined,
    autoPinScreenSharingExistLeaderModeOn: boolean | '' | undefined,
    suggestPinAnotherHostScreenShareLeaderModeOn: boolean,
  ): boolean {
    return (
      isLocalUserOwnsCurrentScreenSharing ||
      !isScreenTileActive ||
      (currentLeaderUid &&
        !autoPinScreenSharingExistLeaderModeOn &&
        !suggestPinAnotherHostScreenShareLeaderModeOn) ||
      this.dismissShowingOpenPermissionsPanelNotification(isUserHost, isCurrentUserHost)
    );
  }

  private dismissShowingOpenPermissionsPanelNotification(
    isUserHost: boolean,
    isCurrentUserHost: boolean,
  ): boolean {
    return !isUserHost && isCurrentUserHost && !this.getCurrentScreenSharePermissionStatus();
  }

  private getCurrentScreenSharePermissionStatus(): boolean {
    return !!this.spaceRepo.activeSpaceCurrentRoom?.permissions.screenShare;
  }

  private handleShowingScreenSharingStartedNotification(
    isCurrentUserHost: boolean,
    isUserHost: boolean,
    screenTile: UserTile,
    autoPinScreenSharingExistLeaderModeOff: boolean,
    suggestPinAnotherHostScreenShareLeaderModeOn: boolean,
    isLeaderModeActive: boolean,
  ) {
    const title = this.constructScreenSharingStartedNotificationTitle(
      screenTile.user$.getValue().name,
    );

    const actionButton = this.constructScreenSharingStartedNotificationActionButton(
      isCurrentUserHost,
      isUserHost,
      screenTile,
      autoPinScreenSharingExistLeaderModeOff,
      suggestPinAnotherHostScreenShareLeaderModeOn,
      isLeaderModeActive,
    );

    this._screenShareTileToBePinedByNotification = screenTile;
    const allowPinScreenActionForObservers = !autoPinScreenSharingExistLeaderModeOff;
    this.buildScreenSharingStartedNotification(
      title,
      actionButton,
      allowPinScreenActionForObservers,
    );
  }

  private constructScreenSharingStartedNotificationTitle(
    screenSharerName: string,
  ): IconMessageToasterElement {
    return new IconMessageToasterElement(
      { icon: 'screen_share', size: 16 },
      this.translateService.instant(`${screenSharerName} is sharing their screen`),
    );
  }

  private constructScreenSharingStartedNotificationActionButton(
    isCurrentUserHost: boolean,
    isUserHost: boolean,
    screenTile: UserTile,
    autoPinScreenSharingExistLeaderModeOff: boolean,
    suggestPinAnotherHostScreenShareLeaderModeOn: boolean,
    isLeaderModeActive: boolean,
  ): NotificationActionButtonsConfig | undefined {
    // actionButton will have a value if:
    // 1. No available screen share currently, and the user who shares his screen isn't a host (isCurrentUserHost && !isUserHost)
    // 2. The call has other screen shares (!autoPinScreenSharingExistLeaderModeOff)
    // Both must be happened while leader mode is OFF
    // 3. There is a suggestion to pin another host's screen sharing while the local user is the host
    if (
      (((isCurrentUserHost && !isUserHost) || !autoPinScreenSharingExistLeaderModeOff) &&
        !isLeaderModeActive) ||
      suggestPinAnotherHostScreenShareLeaderModeOn
    ) {
      return {
        primary: {
          text: autoPinScreenSharingExistLeaderModeOff
            ? this.translateService.instant('Manage their permissions')
            : this.translateService.instant('Pin their screen'),
          icon: autoPinScreenSharingExistLeaderModeOff ? undefined : 'push_pin',
          iconConfig: autoPinScreenSharingExistLeaderModeOff
            ? undefined
            : {
                type: IconTypes.matIcon,
              },
          onClick: () => {
            if (autoPinScreenSharingExistLeaderModeOff) {
              if (
                this.sessionSharedDataService.changeRightPanelView.getValue() !==
                PanelView.participantsManager
              ) {
                this.sessionSharedDataService.changeRightPanelView.next(
                  PanelView.participantsManager,
                );
              }
              this.sessionSharedDataService.changeParticipantsManagerSection.next(
                Section.PERMISSIONS,
              );
            } else {
              this.handleAutoExpand(screenTile, true);
            }
          },
          close: true,
        },
      };
    }

    return undefined;
  }

  private buildScreenSharingStartedNotification(
    title: IconMessageToasterElement,
    actionButton: NotificationActionButtonsConfig | undefined,
    allowActionsForObservers: boolean = false,
  ) {
    this.notificationToasterService.dismissNotificationsByCode([INFOS.SCREEN_SHARING_STARTED]);

    // Configure the screen share icon for V2 notification
    const notificationIcon: NotificationIconConfig = {
      assetName: 'screen_share',
      size: 18,
      type: NotificationIconTypes.MAT_ICON,
      outlined: true,
      background: IconBackground.INFO,
    };

    // Extract the title and description text from the legacy elements
    const titleText = title.msg || '';

    // Build V2 notification
    const notificationDataBuilder = new V2NotificationDataBuilder(INFOS.SCREEN_SHARING_STARTED)
      .title(titleText)
      .icon(notificationIcon)
      .timeOut(8)
      .priority(500);

    // Handle action button if present - it's already in V2 format
    if (actionButton) {
      notificationDataBuilder.actionButtonsConfig(actionButton);
    }

    if (allowActionsForObservers) {
      notificationDataBuilder.allowActionsForObservers();
    }

    this.notificationToasterService.showNotification(notificationDataBuilder.build());
  }

  private syncOrderedParticipantArray(raiseHandReorderFlag: boolean): UserTile[] {
    const previous = this.participantsArray$.getValue();
    const lookup = this.participantLookup;

    if (!raiseHandReorderFlag || previous.length === 0) {
      return Object.values(lookup).flat();
    }

    const stillPresent: UserTile[] = [];
    const participantIdsInPrevious = new Set<string>();

    for (const tile of previous) {
      const participantId = tile.participant$.getValue()?.participantId;
      if (participantId != null && lookup[participantId]) {
        stillPresent.push(tile);
        participantIdsInPrevious.add(participantId);
      }
    }

    const tilesForNewJoiners: UserTile[] = [];
    for (const participantId of Object.keys(lookup)) {
      if (!participantIdsInPrevious.has(participantId)) {
        tilesForNewJoiners.push(...lookup[participantId]);
      }
    }

    return [...stillPresent, ...tilesForNewJoiners];
  }

  // update view functions
  updateParticipantsArray(): void {
    const raiseHandReorderFlag =
      this.flagsService.isFlagEnabled(FLAGS.VC_TILE_ORDERING) &&
      !!this.flagsService.featureFlagsVariables?.vc_tile_ordering?.raise_hand_reorder;
    const usersTiles = this.syncOrderedParticipantArray(raiseHandReorderFlag);
    const screenTiles: UserTile[] = [];
    const raisedHandTiles: UserTile[] = [];
    const regularTiles: UserTile[] = [];
    const selfTiles: UserTile[] = [];
    const raisedHandUserIds = this.raiseHandRepositoryService.getOrderedRaisedHandUserIds();
    const localUserId = this.user?._id;

    for (const tile of usersTiles) {
      const track = tile.track$.getValue();
      if (track === TrackType.SCREEN) {
        screenTiles.push(tile);
        continue;
      }

      const userId = tile.user$.getValue()._id;
      if (userId === localUserId) {
        selfTiles.push(tile);
      } else if (raiseHandReorderFlag && raisedHandUserIds.has(userId)) {
        raisedHandTiles.push(tile);
      } else {
        regularTiles.push(tile);
      }
    }

    const allTiles = [...screenTiles, ...selfTiles, ...raisedHandTiles, ...regularTiles];
    this.participantsArray$.next(allTiles);
    this.updateNumberOfTiles();
  }

  private updateTileVisibility(tile: UserTile, currentActiveRemoteVideoOnCanvas?: Set<string>) {
    // If this video feed is inserted into canvas, then skip unsubscribing from it while paginating tiles
    if (
      !tile.visible$.getValue() &&
      currentActiveRemoteVideoOnCanvas?.has(
        `${tile.participant$.getValue()?.participantId}:${tile.track$.getValue()}`,
      )
    ) {
      return;
    }
    if (tile.participant$.getValue()?.participantId) {
      this.videoCallTracksStateService.updateTrackSubscription(
        tile.participant$.getValue()?.participantId,
        tile.track$.getValue(),
        tile.visible$.getValue(),
        tile.participant$.getValue().local,
        (this.disableVideoStreams && tile.track$.getValue() === 'video') ||
          tile.pipVideo$.getValue()
          ? false
          : true,
      );
    }
  }

  disableIncomingVideoStreams() {
    // Disable the current expanded user video feed who is full screened/pinned
    // We are doing this if we pinned the participant's tile not his screen sharing tile
    // Only caring about the other participants not the local one as we diabling his video feed by turning off his cam not unsubcribing
    if (
      this.expandedScreenUser$.getValue()?.participant$.getValue()?.participantId &&
      this.expandedScreenUser$.getValue()?.user$.getValue()?._id !== this.user?._id &&
      this.expandedScreenUser$.getValue()?.track$.getValue() === TrackType.VIDEO
    ) {
      this.videoCallTracksStateService.updateTrackSubscription(
        this.expandedScreenUser$.getValue()!.participant$.getValue()?.participantId,
        this.expandedScreenUser$.getValue()!.track$.getValue(),
        false,
        this.expandedScreenUser$.getValue()!.participant$.getValue().local,
      );
    }

    for (const tile of this.participantsArray$.getValue()) {
      if (
        tile.active$.getValue() &&
        tile.visible$.getValue() &&
        tile.track$.getValue() === 'video' &&
        tile.participant$.getValue()?.participantId
      ) {
        this.videoCallTracksStateService.updateTrackSubscription(
          tile.participant$.getValue()?.participantId,
          tile.track$.getValue(),
          false,
          tile.participant$.getValue().local,
        );
      }
    }
  }

  enableIncomingVideoStreams() {
    // Enable the current expanded user video feed who is full screened/pinned
    // We are doing this if we pinned the participant's tile not his screen sharing tile
    // Only caring about the other participants not the local one
    if (
      this.expandedScreenUser$.getValue()?.participant$.getValue()?.participantId &&
      this.expandedScreenUser$.getValue()?.user$.getValue()?._id !== this.user?._id &&
      this.expandedScreenUser$.getValue()?.track$.getValue() === TrackType.VIDEO
    ) {
      this.videoCallTracksStateService.updateTrackSubscription(
        this.expandedScreenUser$.getValue()!.participant$.getValue()?.participantId,
        this.expandedScreenUser$.getValue()!.track$.getValue(),
        this.expandedScreenUser$.getValue()!.visible$.getValue(),
        this.expandedScreenUser$.getValue()!.participant$.getValue().local,
      );
    }

    for (const tile of this.participantsArray$.getValue()) {
      if (
        tile.active$.getValue() &&
        tile.track$.getValue() === 'video' &&
        tile.participant$.getValue()?.participantId &&
        tile.visible$.getValue()
      ) {
        this.videoCallTracksStateService.updateTrackSubscription(
          tile.participant$.getValue()?.participantId,
          tile.track$.getValue(),
          tile.visible$.getValue(),
          tile.participant$.getValue().local,
        );
      }
    }
  }

  async updateNumberOfTiles(tileToBeAutoPinned?: UserTile): Promise<void> {
    let numOfTiles = 0;
    this.participantsArray$.getValue().forEach((tile) => {
      if (tile.active$.getValue()) {
        numOfTiles += 1;
      }
    });
    this.oneOnOneModeService.updateTileCount(numOfTiles);

    this.numberOfActualPresentParticipants$.next(numOfTiles);
    if (tileToBeAutoPinned) {
      this.handleAutoExpand(tileToBeAutoPinned, true);
      return;
    }
    this.updateTilesPosition();
  }

  trackById(_: number, item: UserTile): string {
    const id =
      (item.participant$.getValue()
        ? item.participant$.getValue().participantId
        : item.user$.getValue()._id) + item.track$.getValue();
    return id;
  }

  getRole(user: User): string {
    if (user?.personas && user.institution?.personas) {
      return user.institution.personas[user.personas[0]].name;
    } else if (user?.personas?.length) {
      return user.personas[0];
    }
    return '';
  }

  private handleRTCConnectionChange(connected: boolean): void {
    if (this.rtcConnected === undefined && connected) {
      this.displayFirstConnectionToCallNotification();
    } else if (!this.rtcConnected && connected) {
      if (!this.videoCallTileStateManagerService.callConnected) {
        this.displayReconnectedToCallNotification();
      }
    } else if (this.rtcConnected === undefined && !connected) {
      return;
    }

    // We want to reset the maps when the client is closed
    this.rtcConnected = connected;

    if (!this.rtcConnected) {
      const shouldPreserveSessionView = this.shouldPreserveSessionViewAfterCallDisconnect();
      if (
        !shouldPreserveSessionView &&
        (this.expandedScreenUser$.getValue() ||
          this.videoCallTileStateManagerService.currentSessionView === SessionView.EXPANDED)
      ) {
        this.expandedScreenUser$.next(undefined);
        this.returnToWhiteboard();
      }
      this.participantLookup = {};
      this.updateParticipantsArray();
    }
  }

  private handleSpaceOrBreakoutRoomChange(space: ISession, breakoutRoomId?: string) {
    const spaceId = space._id;
    this.setupPresenceListeners(space._id, breakoutRoomId);

    this.spaceId = spaceId;
    this.currentBreakoutRoomId = breakoutRoomId;
    this.users = space.populatedUsers;
    this.setUserLookup();
    this.updateParticipantsArray();
  }

  private setupPresenceListeners(spaceId: string, breakoutRoomId?: string) {
    if (this.spaceId === spaceId && this.currentBreakoutRoomId === breakoutRoomId) {
      return;
    }
    this.presenceSubscription?.unsubscribe();
    this.presenceSubscription = this.presenceProvider
      .getRoomPresenceActivity(spaceId, breakoutRoomId)
      .pipe(untilDestroyed(this))
      .subscribe((presenceResponse) => {
        presenceResponse = presenceResponse ?? new Set();
        // Remember `participant count` before update
        const oldParticipantCount = this.presentUsersUIDs.size;
        this.presentUsersUIDs = presenceResponse;
        const participantCount = this.presentUsersUIDs.size;

        // check to see if presence info has changed, if so, let Fullstory know
        if (participantCount !== oldParticipantCount) {
          const count = {
            'Old count': oldParticipantCount,
            'New count': participantCount,
          };
          // Ignore if freshpaint not enabled.
          this.telemetry.event('Space participant count changed', { count: count });
        }
      });
  }

  public handleExitCurrentScreen(fullscreen: boolean): void {
    if (!fullscreen) {
      return;
    }
    if (this.fullScreenAppManagerService.isFullscreenAppInCallMode()) {
      this.returnToWhiteboard();
      return;
    }
    this.handleFullscreen(this.expandedScreenUser$.getValue(), true);
  }

  returnToWhiteboard(): void {
    if (this.sessionSharedDataService.sessionView.getSessionView() === SessionView.FULLSCREEN_APP) {
      return;
    }

    if (this.expandedScreenUser$.getValue()) {
      this.unsetFullScreen(this.expandedScreenUser$.getValue()!);
      this.expandedScreenUser$.next(undefined);
    }

    this.sessionSharedDataService.sessionView.switchToSessionView(SessionView.WHITEBOARD);

    if (this.currentLayout === VideoLayout.TOP) {
      this.sessionSharedDataService.controlsLayout.next(VideoLayout.TOP);
    }
  }

  private detectNoMic() {
    this.deviceChangeListener = this.devicesManagerService.subscribeToDeviceChange(() => {
      this.rtcServiceController.getDevices().then((devices) => {
        if (devices.mics.length === 0) {
          this.localTracksManager.mute(DeviceType.AUDIO);
          this.localTracksManager.updateMicNotFound(true);
        } else {
          this.localTracksManager.updateMicNotFound(false);
        }
      });
    });
  }

  navigateTiles(pageIndex: number): void {
    this.currentUserTilesPageIndex$.next(pageIndex);
    if (this.currentUserTilesPageIndex$.getValue() < 0) {
      this.currentUserTilesPageIndex$.next(0);
    }
    if (this.currentUserTilesPageIndex$.getValue() >= this.numberOfUserTilesPages$.getValue()) {
      this.currentUserTilesPageIndex$.next(this.numberOfUserTilesPages$.getValue() - 1);
    }
    this.updateTilesPosition();
  }

  /**
   * Publishes pinned/fullscreen screenshare context for the annotation toolbar menu.
   * Action dispatch is handled centrally in `setupVideoTileMenuActions`.
   */
  private setupVideoTileMenu(): void {
    combineLatest([
      this.expandedScreenUser$,
      this.sessionSharedDataService.sessionView.current$.pipe(
        distinctUntilChanged((prev, curr) => prev.view === curr.view),
      ),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([expandedScreenUser, sessionViewState]) => {
        const participantId = expandedScreenUser?.participant$.getValue()?.participantId;
        const trackType = expandedScreenUser?.track$.getValue();
        const isPinnedOrFullscreenScreenshare =
          !!participantId &&
          trackType === TrackType.SCREEN &&
          (sessionViewState.view === SessionView.EXPANDED ||
            sessionViewState.view === SessionView.FULLSCREEN);

        this.videoTileMenuService.setTileContext(
          isPinnedOrFullscreenScreenshare
            ? {
                participantId,
                trackType,
                isPinned: sessionViewState.view === SessionView.EXPANDED,
                isFullscreen: sessionViewState.view === SessionView.FULLSCREEN,
              }
            : null,
        );
      });
  }

  /**
   * Single subscription that dispatches video tile menu actions to the existing tile
   * handlers, resolving the target tile from the published `tileContext`.
   */
  private setupVideoTileMenuActions(): void {
    this.videoTileMenuService.action$.pipe(untilDestroyed(this)).subscribe((action) => {
      if (this.isLeaderModeActive$.getValue()) {
        return;
      }

      const context = this.videoTileMenuService.tileContext;
      if (!context) {
        return;
      }

      const tile = this.resolveTileFromContext(context);

      switch (action) {
        case VideoTileMenuAction.TOGGLE_PIN:
          if (tile) {
            this.handleExpand(tile, true);
          }
          break;
        case VideoTileMenuAction.TOGGLE_FULLSCREEN:
          this.handleFullscreen(tile, true);
          break;
        case VideoTileMenuAction.INSERT_VIDEO:
          this.addRemoteVideo(context.participantId, context.trackType);
          break;
        case VideoTileMenuAction.SCREENSHOT:
          this.handleScreenshot(tile);
          break;
      }
    });
  }

  private resolveTileFromContext(context: VideoTileMenuContext): UserTile | undefined {
    return (
      this.participantsArray$
        .getValue()
        .find(
          (tile) =>
            tile.participant$.getValue()?.participantId === context.participantId &&
            tile.track$.getValue() === context.trackType,
        ) ?? this.expandedScreenUser$.getValue()
    );
  }

  public handleScreenshot(tile: UserTile | undefined): void {
    const participantId = tile?.participant$.getValue()?.participantId;
    if (!participantId) {
      return;
    }

    const screenShareVideoElement = RtcServiceController.getVideoElement(
      participantId,
      TrackType.SCREEN,
    );
    if (screenShareVideoElement) {
      // Defer so the menu closes before the capture.
      modifiedSetTimeout(
        () =>
          this.screenshotService.takeScreenshot(
            screenShareVideoElement,
            screenShareVideoElement.videoWidth,
            screenShareVideoElement.videoHeight,
          ),
        500,
      );
    }
  }

  openGalleryView(): void {
    if (this.expandedScreenUser$.getValue()) {
      this.unsetFullScreen(this.expandedScreenUser$.getValue()!);
      this.expandedScreenUser$.next(undefined);
    }
    this.sessionSharedDataService.sessionView.switchToSessionView(SessionView.GALLERY_VIEW);
  }

  expandVideoTower(): void {
    this.sessionSharedDataService.controlsLayout.next(
      this.isMobileView() ? VideoLayout.TOP : VideoLayout.RIGHT,
    );
  }

  navigateToPreviousView(): void {
    if (this.expandedScreenUser$.getValue()) {
      this.unsetFullScreen(this.expandedScreenUser$.getValue()!);
      this.expandedScreenUser$.next(undefined);
    }
    this.sessionSharedDataService.sessionView.switchBackToPreviousView();
  }

  // handle users view state changes functions
  private unsetFullScreen(userTile: UserTile): boolean {
    const participant = userTile.participant$.getValue();

    if (!participant) {
      return false;
    }
    if (userTile.user$.getValue()._id === this.user?._id) {
      this.videoAiService.unsetFullScreen();
    }
    return true;
  }

  public handleSpeakingParticipant(
    uid: string,
    currentlyDetectingAudio: CurrentlyDetectingAudio,
  ): void {
    if (currentlyDetectingAudio) {
      switch (currentlyDetectingAudio.trackType) {
        case TrackType.AUDIO:
          this.handleAddParticipantSpeaking(uid, currentlyDetectingAudio.isCurrentlyDetected);
          break;
        case TrackType.SCREEN_AUDIO:
          this.handleAddScreenSharingHasAudio(uid, currentlyDetectingAudio.isCurrentlyDetected);
      }
    }
  }

  private handleAddParticipantSpeaking(uid: string, speaking: boolean) {
    if (speaking) {
      this.participantsSpeaking.add(uid);
      this.speakingUser$.next({
        user: this.participantLookup[uid][0]?.user$.getValue(),
        isLocal: !!this.participantLookup[uid][0]?.participant$.getValue()?.local,
      });
      this.sessionSharedDataService.updateBlueBorderActiveSpeaker(uid);
    } else {
      if (this.sessionSharedDataService.getBlueBorderActiveSpeakerVal() === uid) {
        this.sessionSharedDataService.updateBlueBorderActiveSpeaker('');
      }
      this.participantsSpeaking.delete(uid);
    }
  }

  private handleAddScreenSharingHasAudio(uid: string, hasAudio: boolean) {
    if (hasAudio) {
      this.screenSharingHasAudio.add(uid);
    } else {
      this.screenSharingHasAudio.delete(uid);
    }
  }

  sessionEventReceived(event: SessionEvent, userId: string): void {
    switch (event.type) {
      case 'users:network-quality':
        this.participantsArray$
          .getValue()
          .filter((tiles) => tiles.user$.getValue()?._id === userId)
          .forEach((tile) => {
            tile.networkQuality$.next(event.data);
          });

        if (
          this.expandedScreenUser$.getValue() &&
          this.expandedScreenUser$.getValue()?.user$.getValue()._id === userId
        ) {
          this.expandedScreenUser$.getValue()!.networkQuality$.next(event.data);
        }
        break;
      case 'users:emote':
        this.participantsArray$
          .getValue()
          .filter((tiles) => tiles.user$.getValue()?._id === userId)
          .forEach((tile) => {
            tile.emojiId$.next(this.showOtherParticipantsEmotes ? event.data.emojiId : -1);
            // Clear emote in case sender loses network connection
            this.setEmoteExpiry(tile, false);
          });

        if (
          this.expandedScreenUser$.getValue() &&
          this.expandedScreenUser$.getValue()?.user$.getValue()._id === userId
        ) {
          this.expandedScreenUser$
            .getValue()!
            .emojiId$.next(this.showOtherParticipantsEmotes ? event.data.emojiId : -1);
          this.setEmoteExpiry(this.expandedScreenUser$.getValue()!, true);
        }
        break;
      case 'users:celebrate': {
        const eventRoomUid = event.data.breakoutRoomUid;
        const currentRoomUid = this.spaceRepo.activeSpaceCurrentRoomUid;
        if (
          eventRoomUid === undefined ||
          eventRoomUid === null ||
          eventRoomUid === currentRoomUid
        ) {
          this.toggleCelebration(event.data.celebrationId);
        }
        break;
      }
    }
  }

  public toggleCelebration(celebrationId: number) {
    if (!this.videoCallTileStateManagerService.callConnected) {
      return;
    }
    this.celebrationsService.fireCelebration(celebrationId);
  }

  public handleExpand(userTile: UserTile, expand: boolean): void {
    if (!expand) {
      return;
    }
    this.tryHandleExpand(userTile);
  }

  public tryHandleExpand(userTile: UserTile): void {
    if (
      !this.fullScreenTileIsIdentical(userTile) &&
      this.turnOnFullScreen(userTile) &&
      userTile.participant$.getValue()
    ) {
      this.sessionSharedDataService.sessionView.switchToSessionView(SessionView.EXPANDED, {
        tileData: {
          trackType: userTile.track$.getValue(),
          participantId: userTile.participant$.getValue()?.participantId,
          userId: userTile.user$.getValue()._id,
        },
        id: uuidv4(),
      });
    } else {
      this.openGalleryView();
    }
  }

  // Used to handle auto expand from
  // 1. Leader mode
  // 2. Follow mode
  // 3. Auto-pin screen sharing
  public handleAutoExpand(userTile: UserTile, expand: boolean): void {
    if (this.fullScreenTileIsIdentical(userTile) || !expand) {
      return;
    }

    if (this.turnOnFullScreen(userTile)) {
      // Make sure we already subscribed to the pinned tile
      if (userTile.participant$.getValue() && !this.disableVideoStreams) {
        this.videoCallTracksStateService.updateTrackSubscription(
          userTile.participant$.getValue()?.participantId,
          userTile.track$.getValue(),
          true,
          userTile.participant$.getValue().local,
        );
        this.sessionSharedDataService.sessionView.switchToSessionView(SessionView.EXPANDED, {
          tileData: {
            trackType: userTile.track$.getValue(),
            participantId: userTile.participant$.getValue()?.participantId,
            userId: userTile.user$.getValue()._id,
          },
          id: uuidv4(),
        });
      }
    }
  }

  // Core method: Handle PiP video request
  public async openPiPVideo(userTile: UserTile, requestPipVideo: boolean): Promise<void> {
    const participantId = userTile.participant$.getValue()?.participantId;
    if (!participantId) {
      return;
    }

    try {
      if (!requestPipVideo) {
        // Close PiP
        this.closeCurrentVideoPiP();
        userTile.pipVideo$.next(false);
      } else {
        // Open PiP
        const success = await this.pictureInPictureService.openPipVideo(participantId);
        userTile.pipVideo$.next(success);
        // Update cycling state when manually opening PiP
        if (success) {
          this.currentPiPParticipantId = participantId;
        }
      }
    } catch (error) {
      console.error('PiP operation failed:', error);
      userTile.pipVideo$.next(false);
    }
  }

  // Update tile state when PiP closes
  private updateTilePiPState(participantId: string, isPiPActive: boolean): void {
    for (const tile of this.participantsArray$.getValue()) {
      if (
        tile.participant$.getValue()?.participantId === participantId &&
        tile.track$.getValue() === TrackType.VIDEO
      ) {
        tile.pipVideo$.next(isPiPActive);
        break;
      }
    }
  }

  private fullScreenTileIsIdentical(userTile: UserTile, fullscreen = false) {
    // There is no change in who is expanded or which size
    const sameUser =
      this.expandedScreenUser$.getValue()?.participant$.getValue()?.participantId ===
      userTile.participant$.getValue()?.participantId;
    const sameTrack =
      this.expandedScreenUser$.getValue()?.track$.getValue() === userTile.track$.getValue();
    const currentSize = this.sessionSharedDataService.sessionView.getSessionView();
    const sameSize = fullscreen
      ? currentSize === this.sessionView.FULLSCREEN
      : currentSize === this.sessionView.EXPANDED;
    if (sameSize && sameTrack && sameUser) {
      return true;
    }
    return false;
  }

  private turnOnFullScreen(userTile: UserTile): boolean {
    if (
      !userTile.participant$.getValue() ||
      !userTile.user$.getValue() ||
      !userTile.active$.value
    ) {
      return false;
    }

    if (this.expandedScreenUser$.getValue()) {
      this.unsetFullScreen(this.expandedScreenUser$.getValue()!);
    }

    this.expandedScreenUser$.next({ ...userTile });
    return true;
  }

  public handleFullscreen(userTile: UserTile | undefined, fullscreen: boolean): void {
    if (!fullscreen) {
      return;
    }

    if (userTile) {
      if (!this.fullScreenTileIsIdentical(userTile, fullscreen)) {
        if (this.turnOnFullScreen(userTile) && userTile.participant$.getValue()) {
          this.sessionSharedDataService.changeRightPanelView.next(undefined);
          this.sessionSharedDataService.changeLeftPanelView.next(undefined);
          this.sessionSharedDataService.sessionView.switchToSessionView(SessionView.FULLSCREEN, {
            tileData: {
              trackType: userTile.track$.getValue(),
              participantId: userTile.participant$.getValue()?.participantId,
              userId: userTile.user$.getValue()._id,
            },
            fullscreen,
            id: uuidv4(),
          });
        }
      } else {
        this.navigateToPreviousView();
      }
    }
  }

  public handleFullscreenFromLeaderMode(userTile: UserTile | undefined, fullscreen: boolean): void {
    if ((userTile && this.fullScreenTileIsIdentical(userTile, fullscreen)) || !fullscreen) {
      return;
    }

    if (userTile) {
      if (this.turnOnFullScreen(userTile)) {
        // Make sure we already subscribed to the full screen tile
        if (userTile.participant$.getValue()) {
          this.videoCallTracksStateService.updateTrackSubscription(
            userTile.participant$.getValue()?.participantId,
            userTile.track$.getValue(),
            true,
            userTile.participant$.getValue().local,
          );
        }
        this.sessionSharedDataService.changeRightPanelView.next(undefined);
        this.sessionSharedDataService.changeLeftPanelView.next(undefined);
      }
      if (userTile.participant$.getValue()) {
        this.sessionSharedDataService.sessionView.switchToSessionView(SessionView.FULLSCREEN, {
          tileData: {
            trackType: userTile.track$.getValue(),
            participantId: userTile.participant$.getValue()?.participantId,
            userId: userTile.user$.getValue()._id,
          },
          fullscreen,
          id: uuidv4(),
        });
      }
    }
  }

  private displayReconnectedToCallNotification() {
    if (
      this.notificationToasterService.checkIfNotificationActiveByCode(ERRORS.RECONNECTING_TO_CALL)
    ) {
      this.notificationToasterService.dismissNotificationsByCode([ERRORS.RECONNECTING_TO_CALL]);

      this.notificationToasterService.showDefaultSuccessNotification(
        this.translateService.instant('Reconnected to call!'),
        SUCCESSES.RECONNECTED_TO_CALL,
      );
    }
  }

  private displayFirstConnectionToCallNotification() {
    // TODO to be implmented when mocks are ready
  }

  resetSessionViewAfterCallDisconnect(callConnected: boolean) {
    if (
      !callConnected &&
      !this.isLeaderModeActive$.getValue() &&
      this.sessionSharedDataService.sessionView.getSessionView() !== SessionView.FULLSCREEN_APP &&
      !this.isCurrentSessionViewFullscreenAppInCall()
    ) {
      this.sessionSharedDataService.sessionView.switchToSessionView(
        SessionView.WHITEBOARD,
        undefined,
      );
    }
  }

  private isCurrentSessionViewFullscreenAppInCall(): boolean {
    if (!this.fullScreenAppManagerService.isFullScreenAppInCallEnabled()) {
      return false;
    }

    return isFullScreeningAppInCall(
      this.sessionSharedDataService.sessionView.getCurrentSessionViewState(),
    );
  }

  private shouldPreserveSessionViewAfterCallDisconnect(): boolean {
    if (this.isCurrentSessionViewFullscreenAppInCall()) {
      return true;
    }

    return (
      this.isLeaderModeActive$.getValue() &&
      GALLERY_VIEW_RELATED_VIEWS.includes(
        this.sessionSharedDataService.sessionView.getSessionView(),
      )
    );
  }

  resetSessionViewAfterLeaderModeIsDisabled() {
    if (
      !this.rtcConnected &&
      this.sessionSharedDataService.sessionView.getSessionView() !== SessionView.FULLSCREEN_APP &&
      !this.breakoutRoomsManagementService.isBreakoutRoomsManagementScreenShown()
    ) {
      this.sessionSharedDataService.sessionView.switchToSessionView(
        SessionView.WHITEBOARD,
        undefined,
      );
    }
  }

  setEmoteExpiry(userTile: UserTile, isExpandedScreen: boolean) {
    // Clear emote in case sender loses network connection
    if (!this.showOtherParticipantsEmotes) {
      return;
    }
    const userId = userTile.user$.getValue()._id;
    if (this.emotesExpiryTimerMap.has(userId)) {
      clearTimeout(this.emotesExpiryTimerMap.get(userId)!);
    }

    this.emotesExpiryTimerMap.set(
      userId,
      modifiedSetTimeout(() => {
        if (
          isExpandedScreen &&
          this.expandedScreenUser$.getValue()?.user$.getValue()._id !== userId
        ) {
          return;
        }
        userTile.emojiId$.next(-1);
      }, 5000),
    );
  }

  private setupVideoPanelSizeObservable() {
    this.containerResizeObserver = new ResizeObserver(() => {
      this.getVideoSectionWidth(
        this.navService.showSessionAnalytics.value,
        this.pictureInPictureService.isPIPActive,
        this.breakoutRoomsManagementService.isBreakoutRoomsManagementScreenShown(),
      );
    });
    const videoSectionWidth = document.getElementById('parent-container');
    if (videoSectionWidth) {
      this.containerResizeObserver.observe(videoSectionWidth);
    }
  }

  private setupRightSessionsPanelSizeObservable() {
    this.rightSessionsPanelResizeObserver = new ResizeObserver(() => {
      this.getVideoSectionWidth(
        this.navService.showSessionAnalytics.value,
        this.pictureInPictureService.isPIPActive,
        this.breakoutRoomsManagementService.isBreakoutRoomsManagementScreenShown(),
      );
    });
    const videoSectionWidth = document.getElementById('right-sessions-panel');
    if (videoSectionWidth) {
      this.rightSessionsPanelResizeObserver.observe(videoSectionWidth);
    }
  }

  private getVideoSectionWidth(
    showAnalytics: boolean,
    isPIPActive: boolean,
    isBreakoutRoomsManagementScreenShown: boolean,
  ) {
    const videoSectionWidth = document.getElementById('parent-container')?.offsetWidth || 0;
    // right panel width is 0 when its rendered as a bottom sheet
    const rightSessionsPanelWidth = this.screenSizeService.isPanelsBottomSheetBreakpointReached
      ? 0
      : document.getElementById('right-sessions-panel')?.offsetWidth || 0;
    const toarsterPossition = videoSectionWidth + rightSessionsPanelWidth;

    const currentSessionView = this.videoCallTileStateManagerService.currentSessionView;
    if (
      !this.videoCallTileStateManagerService.callConnected ||
      (this.videoCallTileStateManagerService.callConnected &&
        GALLERY_VIEW_RELATED_VIEWS.includes(currentSessionView))
    ) {
      return this.updateToasterHorizontalPosition(rightSessionsPanelWidth + 8);
    }

    if (
      !this.isMobileView() &&
      this.videoCallTileStateManagerService.callConnected &&
      (currentSessionView === SessionView.WHITEBOARD ||
        currentSessionView === SessionView.FULLSCREEN_APP) &&
      (this.sessionSharedDataService.controlsLayout.value === VideoLayout.RIGHT ||
        this.sessionSharedDataService.controlsLayout.value === VideoLayout.MINIMIZED) &&
      !showAnalytics &&
      !isPIPActive &&
      !isBreakoutRoomsManagementScreenShown
    ) {
      return this.updateToasterHorizontalPosition(toarsterPossition + 16);
    } else {
      return this.updateToasterHorizontalPosition(8);
    }
  }

  private updateToasterHorizontalPosition(value: number) {
    document.documentElement.style.setProperty('--toaster-horizontal-position', `${value}px`);
  }

  public activateVideoTowerResizer(el: HTMLElement) {
    const currentSessionView = this.videoCallTileStateManagerService.currentSessionView;
    if (
      currentSessionView !== SessionView.WHITEBOARD &&
      currentSessionView !== SessionView.FULLSCREEN_APP
    ) {
      return;
    }
    this.domListener.resumeListeningToEvent(
      window,
      'mousemove',
      (event) => this.mouseMoveHandler(event, el),
      true,
    );
    this.domListener.resumeListeningToEvent(window, 'mouseup', () => this.mouseUpHandler(), true);
  }

  mouseUpHandler = () => {
    document.body.style.cursor = 'auto';
    document.body.style.userSelect = 'auto';
    this.domListener.pauseListeningToEvent(window, 'mousemove');
    this.domListener.pauseListeningToEvent(window, 'mouseup');
  };

  mouseMoveHandler = (e: MouseEvent, el: HTMLElement) => {
    if (this.isMobileView() || !this.sessionSharedDataService.isAppInWhiteboardView()) {
      return;
    }
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    const maxWidthPercentage = window.innerWidth * 0.5;
    let mousePositionPercentage = e.clientX + 8;

    if (this.directionForLocale$.getValue() === 'ltr') {
      mousePositionPercentage = window.innerWidth - mousePositionPercentage;
    }

    if (mousePositionPercentage > maxWidthPercentage) {
      mousePositionPercentage = maxWidthPercentage;
    }
    if (mousePositionPercentage < this.resizedRightViewMinWidth$.getValue()) {
      mousePositionPercentage = this.resizedRightViewMinWidth$.getValue();
    }
    el.style.width = `${mousePositionPercentage}px`;
  };

  private showRecorderModal(
    recorderDialogParams: any,
    subscriber: Subscriber<File | undefined>,
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      this.modalManagerService.showModal(WbRecorderComponent, recorderDialogParams, {
        afterClosed: (pencilFilesUpload: File | undefined) => {
          if (!pencilFilesUpload) {
            this.spaceRecordingService.clearRecordingOutput();
          }
          subscriber.next(pencilFilesUpload);
          subscriber.complete();
          resolve();
        },
      });
    });
  }

  private displayFinishedRecordingModal(
    recordingOutput: RecordingOutput,
  ): Observable<File | undefined> {
    // Auto-download and skip the modal when auto-local recording is enabled, or when leaving the
    // space (flag set by the leave flow). Read it here so it's cleared even on a no-file stop.
    const shouldAutoDownload =
      this.actionsQueryParamsService.checkIfAutoLocalRecordingIsEnabled() ||
      this.spaceRecordingService.consumeAutoDownloadNextLocalRecording();
    return new Observable((subscriber) => {
      matchI(recordingOutput)({
        file: ({ file, recordingId }) => {
          if (shouldAutoDownload) {
            // Don't delete from IndexedDB for auto-downloads - keep it for user to access later
            this.spaceRecordingService.saveRecordingLocally(file, recordingId, false);
            subscriber.next(undefined);
            subscriber.complete();
            return;
          }
          const recorderDialogParams = {
            width: '700px',
            minHeight: '306px',
            panelClass: 'record-wb-dialog',
            disableClose: true,
            data: {
              file,
              user: this.user,
              title: this.spaceRepo.activeSpace?.title ?? '',
              reason: recordingOutput.reason,
              recordingId: recordingId,
            },
          };

          if (
            this.endEventRedirectManagerService.shouldAddToPreRedirectTasks(
              this.eventStateManagerService.activeEvent,
            )
          ) {
            this.preRedirectTaskId = this.endEventRedirectManagerService.addPreRedirectTask({
              performTask: () => this.showRecorderModal(recorderDialogParams, subscriber),
            });
          } else {
            this.showRecorderModal(recorderDialogParams, subscriber);
          }
        },
        none: () => {
          subscriber.next(undefined);
          subscriber.complete();
        },
      });
    });
  }

  uploadRecordingToVimeo(file: File): void {
    this.uploadService.incrementUploadCounter();
    const uploadId = uuidv4();

    this.vimeoUploadService
      .createVideo(file)
      .pipe(untilDestroyed(this))
      .subscribe(async (response) => {
        try {
          const { progress, uploadTask, uploadCompleted } =
            this.vimeoUploadService.tusUploadPromise(file, response.body.upload.upload_link);

          if (!uploadTask) {
            throw new Error('No upload task received');
          }

          // This just creates the UI for the upload
          this.uploadService.createUploadTaskObjects(
            uploadId,
            file,
            progress.pipe(map((x) => ({ progress: x }))),
            () => uploadTask.abort(),
          );

          uploadTask.start();

          await uploadCompleted;

          this.createRecordingResource({
            url: response.body.link,
            name: file.name,
            size: file.size,
          });
        } catch (error) {
          this.uploadService.showUploadFailedNotification(
            UploadFailedNotificationStatus.VIMEO_UPLOAD_FAILED,
          );
          this.uploadService.cancelUpload(uploadId);
        } finally {
          this.uploadService.decrementUploadCounter();
        }
      });
  }

  private createRecordingResource(fileDetails: FileDetails) {
    const resource = {
      url: fileDetails.url,
      name: fileDetails.name,
      size: fileDetails.size,
      metadata: {
        topics_ids: [],
        minGrade: 0,
        maxGrade: 10,
      },
      type: ResourceItemModel.VIDEO,
      acl: { public: false, visibility: 'CLASS' },
    };
    this.resourcesService
      .createResource(resource)
      .pipe(untilDestroyed(this))
      .subscribe(async (res) => {
        if (res.ok && res.body && this.user && this.spaceRepo.activeSpace) {
          await firstValueFrom(
            this.spacesService.sendRecordingURL(
              this.spaceRepo.activeSpace._id,
              this.user._id,
              res.body.resource._id,
            ),
          );

          // remove the file to indicate the upload is finished
          this.spaceRecordingService.clearRecordingOutput();
        }
      });
  }

  isMobileView(): boolean {
    return this.pictureInPictureService.isPIPActive
      ? this.uiService.isPipWindowMobile
      : this.uiService.isMobile.value;
  }

  private handleCtrlPShortcut(): void {
    if (!this.providerState.callConnectedValue) {
      return;
    }

    // First priority: Original PiP if supported
    if (
      this.pictureInPictureService.isPopoutSupported &&
      this.pictureInPictureService.showPIPButton
    ) {
      if (this.pictureInPictureService.isPIPActive) {
        // Close PiP if it's already active
        this.pictureInPictureService.closePIP();
      } else {
        // Open PiP if it's not active
        this.pictureInPictureService.openPIP();
      }
      return;
    }

    // Second priority: Video PiP with cycling logic
    if (this.pictureInPictureService.isVideoPictureInPictureSupported()) {
      this.handleVideoPiPCycling();
    }
  }

  private async handleVideoPiPCycling(): Promise<void> {
    // Get current filtered participants in real-time
    const filteredParticipants = this.getFilteredParticipantsForPiP();

    if (filteredParticipants.length === 0) {
      return;
    }

    let nextTile: UserTile;

    if (!this.currentPiPParticipantId) {
      // First time - start with first participant
      nextTile = filteredParticipants[0];
    } else {
      // Find current participant in the fresh list
      const currentIndex = filteredParticipants.findIndex(
        (tile) => tile.participant$.getValue()?.participantId === this.currentPiPParticipantId,
      );

      if (currentIndex === -1) {
        // Current participant not found (maybe left), start from beginning
        nextTile = filteredParticipants[0];
      } else if (currentIndex === filteredParticipants.length - 1) {
        // We're at the last participant, close PiP and reset
        await this.closeCurrentVideoPiP();
        this.currentPiPParticipantId = null;
        return;
      } else {
        // Move to next participant
        nextTile = filteredParticipants[currentIndex + 1];
      }
    }

    // Open PiP for the target participant
    this.currentPiPParticipantId = nextTile.participant$.getValue()?.participantId || null;
    await this.openPiPVideo(nextTile, true);
  }

  private getFilteredParticipantsForPiP(): UserTile[] {
    const isCurrentUserHost = this.spaceRepo.isCurrentUserHost();
    const currentUserId = this.user?._id;

    return this.participantsArray$.getValue().filter((tile) => {
      // Only video tracks (skip screen shares)
      if (tile.track$.getValue() !== TrackType.VIDEO) {
        return false;
      }

      // Only active tiles
      if (!tile.active$.getValue()) {
        return false;
      }

      // Only visible tiles
      if (!tile.visible$.getValue()) {
        return false;
      }

      if (
        !this.videoCallTracksStateService.getParticipantVideoPlayingState(
          tile.participant$.getValue()?.participantId || '',
        )
      ) {
        return false;
      }

      // Skip current user
      if (tile.user$.getValue()._id === currentUserId) {
        return false;
      }

      const tileUserIsHost = Session.isOwnedByUser(
        this.spaceRepo.activeSpace,
        tile.user$.getValue(),
      );

      if (isCurrentUserHost) {
        // Host cycles through participants only (exclude other hosts)
        return !tileUserIsHost;
      } else {
        // Participant cycles through hosts only
        return tileUserIsHost;
      }
    });
  }

  private async closeCurrentVideoPiP(): Promise<void> {
    if (this.currentPiPParticipantId) {
      await this.pictureInPictureService.closePipVideo(this.currentPiPParticipantId);
      this.currentPiPParticipantId = null;
    }
  }

  private cleanupVideoPiPState(): void {
    this.pictureInPictureService.cleanupVideoPiP();
    this.currentPiPParticipantId = null;
  }

  private listenToUpdateTilesPosition(): void {
    if (!this.fullScreenAppManagerService.isFullScreenAppInCallEnabled()) {
      return;
    }
    this.fullScreenAppManagerService.updateTilesPosition$
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.updateTilesPosition();
      });
  }

  private setupFullScreenAppManager(): void {
    if (!this.fullScreenAppManagerService.isFullScreenAppInCallEnabled()) {
      return;
    }
    this.spaceRepo.activeSpaceSelectedBoardUid$
      .pipe(untilDestroyed(this), filterNil())
      .subscribe(() => this.fullScreenAppManagerService.updateAppGalleryTileAfterBoardChange());

    this.fullScreenAppManagerService.subscribeToFullscreenAppStateChanges();
    this.listenToUpdateTilesPosition();
  }
}
